import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
// Supabase
import { supabase } from "../../supabase/supabase";
import { Link, useNavigate } from "react-router-dom";
import "./Feed.css";
import {
  FaHeart,
  FaShare,
  FaExpand,
  FaEllipsisV,
  FaBookmark,
  FaLink,
  FaEyeSlash,
  FaFlag,
  FaTimes,
  FaPlay,
  FaPause
} from "react-icons/fa";
import CreatePanel from "../../components/CreatePanel/CreatePanel";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import WaveInsideLockedBox from "../../components/Buttons/WaveButton/WaveButton";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { trackPostView, trackPostViewRPC } from "../../utils/trackPostView";

// ---------- helpers ----------
const PAGE_SIZE = 25; // consistent batch size for Detailed/Grid
const HIDDEN_POSTS_KEY = 'feed_hidden_posts';
const BLOCKED_USERS_KEY = 'blocked_user_ids';
// Backend API URL - adjust if your backend runs on a different port/domain
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam', description: 'Misleading or repetitive content' },
  { value: 'false_information', label: 'False information', description: 'Factually incorrect content' },
  { value: 'hate_speech', label: 'Hate speech', description: 'Content that promotes violence or discrimination' },
  { value: 'harassment', label: 'Harassment', description: 'Bullying or abusive behavior' },
  { value: 'violence', label: 'Violence', description: 'Content that promotes or glorifies violence' },
  { value: 'copyright violation', label: 'Copyright violation', description: 'unauthorized use of copyrighted material' },
  { value: 'other', label: 'Other', description: 'Other reason not listed above' }
];

const DEFAULT_USER_ICON =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Rya2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPgo8cGF0aCBkPSJNMjAgMjF2LTJhNCA0IDAgMCAwLTQtNEg4YTQgNCAwIDAgMC00IDR2MiIvPgo8Y2lyY2xlIGN4PSIxMiIgY3k9IjciIHI9IjQiLz4KPC9zdmc+Cjwvc3ZnPg==";

const getDefaultUserIcon = () => DEFAULT_USER_ICON;

// Helper function to fetch user profile data
const fetchUserProfile = async (userId) => {
  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('display_name, username, avatar_url')
      .eq('user_id', userId)
      .single();

    const fallback = {
      display_name: `User ${userId.slice(0, 8)}`,
      username: `user_${userId.slice(0, 8)}`,
      avatar_url: getDefaultUserIcon()
    };

    if (error || !profile) {
      return fallback;
    }

    return {
      ...profile,
      avatar_url: resolveAvatarUrl(profile.avatar_url, userId)
    };
  } catch (err) {
    console.error('[Feed] Error fetching user profile:', err);
    return {
      display_name: `User ${userId.slice(0, 8)}`,
      username: `user_${userId.slice(0, 8)}`,
      avatar_url: getDefaultUserIcon()
    };
  }
};

// Helper function to resolve avatar URL
const resolveAvatarUrl = (filename, userFolderId) => {
  if (!filename) return getDefaultUserIcon();

  // if already a full url, return it
  if (typeof filename === 'string' && /^(http|https):\/\//i.test(filename)) {
    return filename;
  }
  // if data url, return it
  if (typeof filename === 'string' && filename.startsWith('data:')) return filename;

  // Construct the proper path: avatars/<userId>/<filename>
  const path = `${userFolderId}/${filename}`;

  try {
    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    if (data?.publicUrl) {
      return data.publicUrl;
    }
  } catch (e) {
    console.log('[Feed] Error resolving avatar URL:', e);
  }

  return getDefaultUserIcon();
};

const getThumbnailUrl = url => {
  if (!url) return "/default-link-placeholder.png";

  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");

    // YouTube (including Shorts)
    if (host === "youtube.com" || host === "m.youtube.com" || host.endsWith("youtube.com")) {
      const shortsMatch = u.pathname.match(/^\/shorts\/([^\/\?]+)/);
      if (shortsMatch) {
        return `https://img.youtube.com/vi/${shortsMatch[1]}/hqdefault.jpg`;
      }
      const videoId = u.searchParams.get("v");
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      }
      return "/default-youtubeshorts-thumb.jpeg";
    }

    if (host === "youtu.be") {
      const videoId = u.pathname.slice(1);
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      }
      return "/default-youtubeshorts-thumb.jpeg";
    }

    // Instagram
    const ig = url.match(/instagram\.com\/(?:reel|p|tv)\/([^\/?]+)/);
    if (ig) {
      // Return Instagram media URL - will be fetched/validated asynchronously
      return `https://www.instagram.com/p/${ig[1]}/media/?size=l`;
    }

    // TikTok
    const tt = url.match(/tiktok\.com\/@[^\/]+\/video\/(\d+)/);
    if (tt) {
      // Return a placeholder - actual thumbnail will be fetched via oEmbed
      return "/default-tiktok-thumb.jpeg";
    }

    // Facebook (various formats: share links, watch videos, posts, etc.)
    if (host === "facebook.com" || host === "m.facebook.com" || host.endsWith("facebook.com")) {
      // Check for various Facebook link patterns
      const fbPatterns = [
        /facebook\.com\/watch\/\?v=(\d+)/,           // Watch videos
        /facebook\.com\/share\/r\/[^\/]+/,           // Share links
        /facebook\.com\/.*\/videos\/\d+/,            // Video posts
        /facebook\.com\/.*\/posts\/[^\/]+/,          // Posts
        /facebook\.com\/.*\/permalink\/\d+/,         // Permalink posts
        /facebook\.com\/.*\/photos\/[^\/]+/,         // Photo posts
        /facebook\.com\/.*\/reel\/[^\/]+/,           // Reels
      ];

      const isFacebook = fbPatterns.some(pattern => pattern.test(url));
      if (isFacebook) {
        // Return default - actual thumbnail will be fetched via oEmbed
        return "/default-facebook-thumb2.png";
      }
    }
  } catch (err) {
    // Invalid URL, fall through to default
  }

  return "/default-link-placeholder.png";
};

const formatTimestamp = (date) => {
  if (!date) return "";
  const now = new Date();
  const dateObj = date instanceof Date ? date : new Date(date);
  const seconds = Math.floor((now - dateObj) / 1000);
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60
  };
  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval >= 1) return `${interval} ${unit}${interval === 1 ? '' : 's'} ago`;
  }
  return "Just now";
};

const sortPostsByTimestampDesc = (posts) => {
  if (!Array.isArray(posts)) return [];
  return [...posts].sort((a, b) => {
    const aTime = new Date(a?.timestamp || a?.created_at || 0).getTime();
    const bTime = new Date(b?.timestamp || b?.created_at || 0).getTime();
    return bTime - aTime;
  });
};


// List of damaged video post IDs to exclude from fetching
const DAMAGED_VIDEO_POST_IDS = [
  'N9yoCyhpOaXrmdB3tczN',
  'f9TBWbitKirXcBzvVFg0',
  '4QhdkyQ6AmfN66pP3HDs',
  'wDVY2DZn4gZH4IohCKoO',
  'ZBmc6pcNrkMc2hrRiVwy'
];

// Helper function to validate if a post is valid (has required fields)
const isValidSupabasePost = (post) => {
  if (!post || typeof post !== 'object') return false;
  // Check for essential Supabase fields
  if (!post.id || !post.user_id || !post.created_at) return false;
  return true;
};

// Helper function to check if we're offline
const isOffline = () => {
  return typeof navigator !== 'undefined' && !navigator.onLine;
};

// Helper function to check if posts exist AND are public (not private) in database
// When offline, returns all postIds to preserve cached posts (but they'll be filtered by is_private flag)
const validatePostsExist = async (postIds) => {
  if (!postIds || postIds.length === 0) return new Set();

  // If offline, return all post IDs to preserve cached posts
  // Note: filterOutHidden will still filter private posts based on cached is_private flag
  if (isOffline()) {
    console.log('[Feed] Offline detected - skipping post validation, preserving cached posts');
    return new Set(postIds);
  }

  try {
    // Fetch both id and is_private to validate posts exist AND are public
    const { data, error } = await supabase
      .from('posts')
      .select('id, is_private')
      .in('id', Array.from(postIds));

    if (error) {
      console.error('[Feed] Error validating posts:', error);
      // On network error, return all IDs to preserve cached posts
      // This prevents clearing the feed when network fails
      if (error.message?.includes('fetch') || error.message?.includes('network') || error.code === 'PGRST116') {
        console.warn('[Feed] Network error during validation - preserving cached posts');
        return new Set(postIds);
      }
      return new Set(); // Return empty set only for non-network errors
    }

    // Only return posts that exist AND are public (is_private = false)
    const validPublicPostIds = (data || [])
      .filter(post => post.is_private === false)
      .map(post => post.id);

    const invalidCount = (data || []).length - validPublicPostIds.length;
    if (invalidCount > 0) {
      console.log(`[Feed] Filtered out ${invalidCount} private/deleted posts during validation`);
    }

    return new Set(validPublicPostIds);
  } catch (err) {
    console.error('[Feed] Failed to validate posts:', err);
    // On network error, preserve cached posts
    if (err.message?.includes('fetch') || err.message?.includes('network') || err.name === 'TypeError') {
      console.warn('[Feed] Network error during validation - preserving cached posts');
      return new Set(postIds);
    }
    return new Set();
  }
};

// Helper to resolve per‑post audio URL from either `audioUrl` or `audio_filename`
const getPostAudioUrl = (post) => {
  if (!post) return null;

  if (post.audioUrl) return post.audioUrl;

  if (post.audio_filename && post.audio_filename != null && post.audio_filename !== '') {
    const filename = String(post.audio_filename).trim();
    if (filename.length > 0) {
      const userId = post.postedById || post.user_id || post.postedByID || post.userId;
      if (userId) {
        return `https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/post-media/${userId}/${filename}`;
      }
    }
  }

  return null;
};

// ---------- component ----------
export default function Feed() {
  const [feedPosts, setFeedPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [, setSavedPostIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [focusedPost, setFocusedPost] = useState(null);
  const [showPostMenu, setShowPostMenu] = useState(false);
  const [focusedPostMediaIndex, setFocusedPostMediaIndex] = useState(0);
  const [lastCursor, setLastCursor] = useState(null); // { ts, id }
  const [isFocusedAudioPlaying, setIsFocusedAudioPlaying] = useState(false);

  const postIds = useRef(new Set());
  const savedPostIdsRef = useRef(new Set());
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const scrollRestored = useRef(false);
  const profileCacheRef = useRef(new Map());
  const focusedAudioRef = useRef(null);

  // Touch swipe state for mobile navigation (screens <= 768px)
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchEndRef = useRef({ x: 0, y: 0 });
  const isMobileRef = useRef(false);


  const { userProfile, currentUser } = useAuth();
  const { translations: tRaw } = useLanguage();
  const t = useMemo(() => ({ ...tRaw, investment: { ...tRaw?.investment } }), [tRaw]);
  const focusedAudioUrl = useMemo(() => getPostAudioUrl(focusedPost), [focusedPost]);

  const [filterType, setFilterType] = useState(() => {
    const saved = localStorage.getItem('feed_selected_filter');
    return saved || "All";
  });
  const [selectedView, setSelectedView] = useState(() => {
    const saved = localStorage.getItem('feed_selected_view');
    return saved || "Grid";
  });

  const cacheProfileData = useCallback((userId, profileLike) => {
    if (!userId || !profileLike) return null;
    const cached = {
      displayName: profileLike.display_name || profileLike.username || `user_${userId.slice(0, 8)}`,
      username: profileLike.username || `user_${userId.slice(0, 8)}`,
      avatarUrl: profileLike.avatar_url || getDefaultUserIcon()
    };
    profileCacheRef.current.set(userId, cached);
    return cached;
  }, []);

  const fetchAndCacheProfile = useCallback(async (userId) => {
    if (!userId) return null;
    const cached = profileCacheRef.current.get(userId);
    if (cached) return cached;
    const profile = await fetchUserProfile(userId);
    if (!profile) return null;
    return cacheProfileData(userId, profile);
  }, [cacheProfileData]);

  // Masonry data - steady stream approach
  const [masonryPosts, setMasonryPosts] = useState([]);
  const [masonryBuffer, setMasonryBuffer] = useState([]);
  const [masonryLastCursor, setMasonryLastCursor] = useState(null);
  const [masonryHasMore, setMasonryHasMore] = useState(true);
  const masonryLoadingRef = useRef(false);
  const masonryPostIds = useRef(new Set());
  const masonryDisplayIndex = useRef(0);
  const masonryUpdateTimeoutRef = useRef(null);
  // Responsive column counts for Detailed/Grid and Masonry
  const [columnCount, setColumnCount] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1920;
    if (w <= 480) return 2;
    // Use an integer fit based on a minimum desired column width
    const minColWidth = 220; // px – tweak as needed for card width
    return Math.max(2, Math.min(6, Math.floor(w / minColWidth)));
  });
  const [masonryCount, setMasonryCount] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1920;
    if (w <= 480) return 2;
    if (w <= 768) return 3;
    if (w <= 1200) return 4;
    return 6;
  });

  const [hiddenPostIds, setHiddenPostIds] = useState(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const stored = window.localStorage.getItem(HIDDEN_POSTS_KEY);
      if (!stored) return new Set();
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed);
      }
    } catch (e) {
      console.warn('[Feed] Failed to restore hidden posts from storage:', e);
    }
    return new Set();
  });
  const hiddenPostIdsRef = useRef(hiddenPostIds);
  const [blockedUserIds, setBlockedUserIds] = useState(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const stored = window.localStorage.getItem(BLOCKED_USERS_KEY);
      if (!stored) return new Set();
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed);
      }
    } catch (e) {
      console.warn('[Feed] Failed to restore blocked user ids:', e);
    }
    return new Set();
  });
  const blockedUserIdsRef = useRef(blockedUserIds);

  // Fetch hidden posts from Supabase on mount
  useEffect(() => {
    if (!currentUser?.id) return;

    const fetchHiddenPosts = async () => {
      try {
        const { data, error } = await supabase
          .from('hidden_posts')
          .select('post_id')
          .eq('user_id', currentUser.id);

        if (error) throw error;

        if (data && data.length > 0) {
          const hiddenSet = new Set(data.map(item => item.post_id));

          setHiddenPostIds(prev => {
            const next = new Set([...prev, ...hiddenSet]);
            hiddenPostIdsRef.current = next;
            // Sync with local storage to keep it up to date
            if (typeof window !== 'undefined') {
              localStorage.setItem(HIDDEN_POSTS_KEY, JSON.stringify(Array.from(next)));
            }
            return next;
          });

          // Filter out any currently displayed posts that are hidden
          setFeedPosts(prev => prev.filter(post => !hiddenSet.has(post.id)));
          setMasonryPosts(prev => prev.filter(post => !hiddenSet.has(post.id)));
          setMasonryBuffer(prev => prev.filter(post => !hiddenSet.has(post.id)));

          // Update refs to ensure new posts are filtered correctly
          postIds.current = new Set([...postIds.current].filter(id => !hiddenSet.has(id)));
          masonryPostIds.current = new Set([...masonryPostIds.current].filter(id => !hiddenSet.has(id)));
        }
      } catch (err) {
        console.error('[Feed] Failed to fetch hidden posts:', err);
      }
    };

    fetchHiddenPosts();
  }, [currentUser?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncBlockedIds = () => {
      try {
        const stored = window.localStorage.getItem(BLOCKED_USERS_KEY);
        if (!stored) {
          setBlockedUserIds(new Set());
          return;
        }
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setBlockedUserIds(new Set(parsed));
        }
      } catch (e) {
        console.warn('[Feed] Failed to sync blocked user ids:', e);
      }
    };

    const handleCustomEvent = () => syncBlockedIds();
    const handleStorage = (event) => {
      if (event.key === BLOCKED_USERS_KEY) {
        syncBlockedIds();
      }
    };

    window.addEventListener('blocked-users-updated', handleCustomEvent);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('blocked-users-updated', handleCustomEvent);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const [hideConfirmPost, setHideConfirmPost] = useState(null);
  const [reportPost, setReportPost] = useState(null);
  const [selectedReportReason, setSelectedReportReason] = useState(null);
  const [reportDescription, setReportDescription] = useState("");
  const [reportError, setReportError] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);

  useEffect(() => {
    hiddenPostIdsRef.current = hiddenPostIds;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(HIDDEN_POSTS_KEY, JSON.stringify(Array.from(hiddenPostIds)));
      } catch (e) {
        console.warn('[Feed] Failed to persist hidden posts:', e);
      }
    }
  }, [hiddenPostIds]);

  useEffect(() => {
    blockedUserIdsRef.current = blockedUserIds;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(BLOCKED_USERS_KEY, JSON.stringify(Array.from(blockedUserIds)));
      } catch (e) {
        console.warn('[Feed] Failed to persist blocked user ids:', e);
      }
    }
    if (blockedUserIds.size === 0) return;
    const filterByBlocked = (post) => {
      if (!post) return false;
      const ownerId = post.postedById || post.postedByID || post.user_id || post.userId;
      if (ownerId && blockedUserIds.has(ownerId)) return false;
      return true;
    };
    setFeedPosts(prev => {
      const filtered = prev.filter(filterByBlocked);
      if (filtered.length !== prev.length) {
        postIds.current = new Set(filtered.map(post => post.id));
      }
      return filtered;
    });
    setMasonryPosts(prev => {
      const filtered = prev.filter(filterByBlocked);
      if (filtered.length !== prev.length) {
        masonryPostIds.current = new Set(filtered.map(post => post.id));
      }
      return filtered;
    });
    setMasonryBuffer(prev => prev.filter(filterByBlocked));
  }, [blockedUserIds]);

  useEffect(() => {
    feedPosts.forEach(post => {
      const ownerId = post.postedById || post.user_id;
      if (!ownerId) return;
      if (post._needsProfileUpdate) return;
      if (profileCacheRef.current.has(ownerId)) return;
      cacheProfileData(ownerId, {
        display_name: post.postedByUsername,
        username: post.postedByUsername,
        avatar_url: post.postedByProfileImg
      });
    });
  }, [feedPosts, cacheProfileData]);

  useEffect(() => {
    masonryPosts.forEach(post => {
      const ownerId = post.postedById || post.user_id;
      if (!ownerId) return;
      if (post._needsProfileUpdate) return;
      if (profileCacheRef.current.has(ownerId)) return;
      cacheProfileData(ownerId, {
        display_name: post.postedByUsername,
        username: post.postedByUsername,
        avatar_url: post.postedByProfileImg
      });
    });
  }, [masonryPosts, cacheProfileData]);

  useEffect(() => {
    if (!currentUser?.id) {
      setLikedPosts(new Set());
      return;
    }

    const fetchLikedPosts = async () => {
      try {
        const { data, error } = await supabase
          .from('likes')
          .select('post_id')
          .eq('user_id', currentUser.id);

        if (error) {
          console.error('[Feed] Error loading liked posts:', error);
          return;
        }

        const likedSet = new Set((data || []).map(row => row.post_id).filter(Boolean));
        setLikedPosts(likedSet);
      } catch (err) {
        console.error('[Feed] Failed to load liked posts:', err);
      }
    };

    fetchLikedPosts();
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.id) {
      const empty = new Set();
      savedPostIdsRef.current = empty;
      setSavedPostIds(empty);
      return;
    }

    const fetchSavedPosts = async () => {
      try {
        const { data, error } = await supabase
          .from('saves')
          .select('post_id')
          .eq('user_id', currentUser.id);

        if (error) {
          console.error('[Feed] Error loading saved posts:', error);
          return;
        }

        const savedSet = new Set((data || []).map(row => row.post_id).filter(Boolean));
        savedPostIdsRef.current = savedSet;
        setSavedPostIds(savedSet);

        setFeedPosts(prev => prev.map(post => (
          post?.id ? { ...post, isSaved: savedSet.has(post.id) } : post
        )));

        setMasonryPosts(prev => prev.map(post => (
          post?.id ? { ...post, isSaved: savedSet.has(post.id) } : post
        )));

        setFocusedPost(prev => (
          prev?.id ? { ...prev, isSaved: savedSet.has(prev.id) } : prev
        ));
      } catch (err) {
        console.error('[Feed] Failed to load saved posts:', err);
      }
    };

    fetchSavedPosts();
  }, [currentUser?.id]);

  const updatePostLikeCount = useCallback((postId, delta) => {
    if (!delta) return;

    setFeedPosts(prev => prev.map(post => (
      post.id === postId
        ? { ...post, like_count: Math.max(0, (post.like_count || 0) + delta) }
        : post
    )));

    setMasonryPosts(prev => prev.map(post => (
      post.id === postId
        ? { ...post, like_count: Math.max(0, (post.like_count || 0) + delta) }
        : post
    )));

    setFocusedPost(prev => (
      prev?.id === postId
        ? { ...prev, like_count: Math.max(0, (prev.like_count || 0) + delta) }
        : prev
    ));
  }, []);

  const updatePostSavedFlag = useCallback((postId, isSaved) => {
    setFeedPosts(prev => prev.map(post => (
      post.id === postId ? { ...post, isSaved } : post
    )));

    setMasonryPosts(prev => prev.map(post => (
      post.id === postId ? { ...post, isSaved } : post
    )));

    setFocusedPost(prev => (
      prev?.id === postId ? { ...prev, isSaved } : prev
    ));
  }, []);

  const toggleLikePost = useCallback(async (postId) => {
    if (!postId) return;
    if (!currentUser?.id) {
      alert(t?.likeRequiresLogin || 'Please sign in to like this post.');
      return;
    }

    let wasLiked = false;
    setLikedPosts(prev => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
        wasLiked = true;
      } else {
        next.add(postId);
        wasLiked = false;
      }
      return next;
    });

    const delta = wasLiked ? -1 : 1;
    updatePostLikeCount(postId, delta);

    try {
      if (wasLiked) {
        const { error } = await supabase
          .from('likes')
          .delete()
          .eq('user_id', currentUser.id)
          .eq('post_id', postId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('likes')
          .insert({
            user_id: currentUser.id,
            post_id: postId,
            created_at: new Date().toISOString()
          });
        if (error) throw error;
      }
    } catch (error) {
      console.error('[Feed] Error toggling like:', error);
      setLikedPosts(prev => {
        const next = new Set(prev);
        if (wasLiked) {
          next.add(postId);
        } else {
          next.delete(postId);
        }
        return next;
      });
      updatePostLikeCount(postId, -delta);
      alert(t?.likeError || 'Unable to update like right now.');
    }
  }, [currentUser?.id, t, updatePostLikeCount]);

  const toggleSavePost = useCallback(async (postArg) => {
    const postId = typeof postArg === 'string' ? postArg : postArg?.id;
    if (!postId) return;
    if (!currentUser?.id) {
      alert(t?.saveRequiresLogin || 'Please sign in to save this post.');
      return;
    }

    let wasSaved = false;
    setSavedPostIds(prev => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
        wasSaved = true;
      } else {
        next.add(postId);
        wasSaved = false;
      }
      return next;
    });

    const nextIsSaved = !wasSaved;
    updatePostSavedFlag(postId, nextIsSaved);

    const refClone = new Set(savedPostIdsRef.current);
    if (wasSaved) refClone.delete(postId); else refClone.add(postId);
    savedPostIdsRef.current = refClone;

    try {
      if (wasSaved) {
        const { error } = await supabase
          .from('saves')
          .delete()
          .eq('user_id', currentUser.id)
          .eq('post_id', postId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('saves')
          .insert({
            user_id: currentUser.id,
            post_id: postId,
            created_at: new Date().toISOString()
          });
        if (error) throw error;
      }
    } catch (error) {
      console.error('[Feed] Error toggling save:', error);
      setSavedPostIds(prev => {
        const next = new Set(prev);
        if (wasSaved) {
          next.add(postId);
        } else {
          next.delete(postId);
        }
        return next;
      });

      const rollback = new Set(savedPostIdsRef.current);
      if (wasSaved) rollback.add(postId); else rollback.delete(postId);
      savedPostIdsRef.current = rollback;

      updatePostSavedFlag(postId, wasSaved);
      alert(t?.saveError || 'Unable to save this post right now.');
    }
  }, [currentUser?.id, t, updatePostSavedFlag]);

  // Recalculate responsive column counts on resize or view change
  useEffect(() => {
    const calcCounts = () => {
      const w = window.innerWidth;
      const containerWidth = feedContainerRef.current?.clientWidth || w;
      // Grid view: integer fit with min width
      const minGridWidth = 200;
      const nextGridCols = Math.max(2, Math.min(6, Math.floor(containerWidth / minGridWidth)));
      // Detailed view: integer fit, capped 2..6; small screens force 2
      const minDetailedWidth = 220;
      const nextDetailedCols = w <= 480
        ? 2
        : Math.max(2, Math.min(6, Math.floor(containerWidth / minDetailedWidth)));
      setColumnCount(selectedView === 'Grid' ? nextGridCols : nextDetailedCols);
      setMasonryCount(w <= 480 ? 2 : w <= 768 ? 3 : w <= 1200 ? 4 : 6);
    };
    calcCounts();
    window.addEventListener('resize', calcCounts, { passive: true });
    return () => window.removeEventListener('resize', calcCounts);
  }, [selectedView]);

  const masonryColumns = useMemo(() => {
    const cols = Array.from({ length: masonryCount }, () => []);

    if (masonryPosts.length === 0) return cols;

    // Simple round-robin distribution - no complex balancing to avoid re-renders
    masonryPosts.forEach((post, idx) => {
      const colIndex = idx % masonryCount;
      cols[colIndex].push({
        ...post,
        uniqueKey: `${post.id}-${idx}`
      });
    });

    return cols;
  }, [masonryPosts, masonryCount]);

  // containers + sentinel
  const feedContainerRef = useRef(null);
  const feedListRef = useRef(null);
  const sentinelRef = useRef(null);

  // minimized create panel
  const [isMinimized, setIsMinimized] = useState(() => {
    return localStorage.getItem("createPanelMinimized") === "true";
  });
  const [hasSavedContent, setHasSavedContent] = useState(() => {
    const savedContent = localStorage.getItem("createPanelContent");
    return savedContent && savedContent !== "null";
  });
  const handleMinimize = () => {
    setIsMinimized(true);
    localStorage.setItem("createPanelMinimized", "true");
  };
  const handleExpand = () => {
    setIsMinimized(false);
    localStorage.setItem("createPanelMinimized", "false");
  };
  const savePanelContent = (content) => {
    // Check if content is actually empty
    const isEmpty = !content || (
      (!content.text || content.text.trim() === '') &&
      (!content.media || content.media.length === 0) &&
      (!content.linkUrl || content.linkUrl.trim() === '')
    );

    if (isEmpty) {
      localStorage.removeItem("createPanelContent");
      setHasSavedContent(false);
    } else {
      localStorage.setItem("createPanelContent", JSON.stringify(content));
      setHasSavedContent(true);
    }
  };
  const clearSavedContent = () => {
    localStorage.removeItem("createPanelContent");
    setHasSavedContent(false);
  };

  // ---------- Feed Cache Management ----------
  const FEED_CACHE_KEY = 'feed_cache';
  const SCROLL_POSITION_KEY = 'feed_scroll_position';
  const FEED_CURSOR_KEY = 'feed_cursor';
  const MASONRY_CACHE_KEY = 'feed_masonry_cache';
  const MASONRY_CURSOR_KEY = 'feed_masonry_cursor';
  const FEED_CACHE_TIMESTAMP_KEY = 'feed_cache_timestamp';
  const FEED_CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes cache expiry
  const ALL_POSTS_CACHE_KEY = 'feed_all_posts_cache'; // Cache all posts regardless of filter

  const filterOutHidden = useCallback((posts) => {
    if (!Array.isArray(posts) || posts.length === 0) return [];
    const hiddenIds = hiddenPostIdsRef.current;
    const blockedIds = blockedUserIdsRef.current;

    // Filter function to check if post is locked or private
    const isLockedOrPrivate = (post) => {
      return post?.is_locked === true ||
        post?.is_private === true ||
        post?.isLocked === true ||
        post?.isPrivate === true;
    };

    // First filter out invalid posts
    const validPosts = posts.filter(post => isValidSupabasePost(post));

    if (hiddenIds.size === 0 && blockedIds.size === 0) {
      return validPosts
        .filter(post => !isLockedOrPrivate(post))
        .slice()
        .map(post => ({
          ...post,
          isLocked: post?.isLocked ?? Boolean(post?.is_locked),
          isPrivate: post?.isPrivate ?? Boolean(post?.is_private)
        }));
    }
    return validPosts.filter(post => {
      if (!post) return false;
      if (hiddenIds.has(post.id)) return false;
      if (isLockedOrPrivate(post)) return false;
      const ownerId = post.postedById || post.postedByID || post.user_id || post.userId;
      if (ownerId && blockedIds.has(ownerId)) return false;
      return true;
    });
  }, []);

  // Save feed posts to cache (only save valid posts)
  const saveFeedCache = useCallback((posts, cursor, view) => {
    try {
      // Only save valid posts to cache
      const validPosts = posts.filter(post => isValidSupabasePost(post));

      if (view === 'Masonry') {
        localStorage.setItem(MASONRY_CACHE_KEY, JSON.stringify(validPosts));
        if (cursor) {
          localStorage.setItem(MASONRY_CURSOR_KEY, JSON.stringify(cursor));
        }
      } else {
        localStorage.setItem(FEED_CACHE_KEY, JSON.stringify(validPosts));
        if (cursor) {
          localStorage.setItem(FEED_CURSOR_KEY, JSON.stringify(cursor));
        }
      }

      // Also save to all posts cache (for filter switching)
      const allCached = localStorage.getItem(ALL_POSTS_CACHE_KEY);
      const allPosts = allCached ? JSON.parse(allCached) : [];
      const existingIds = new Set(allPosts.map(p => p.id));

      // Merge new posts with existing cache, avoiding duplicates
      const merged = [...allPosts];
      validPosts.forEach(post => {
        if (!existingIds.has(post.id)) {
          merged.push(post);
        } else {
          // Update existing post
          const index = merged.findIndex(p => p.id === post.id);
          if (index !== -1) {
            merged[index] = post;
          }
        }
      });

      // Sort by timestamp and limit cache size (keep last 200 posts)
      const sorted = sortPostsByTimestampDesc(merged).slice(0, 200);
      localStorage.setItem(ALL_POSTS_CACHE_KEY, JSON.stringify(sorted));
      localStorage.setItem(FEED_CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (e) {
      console.warn('[Feed] Failed to save cache:', e);
    }
  }, []);

  // Check if cache is still valid
  const isCacheValid = useCallback(() => {
    try {
      const timestamp = localStorage.getItem(FEED_CACHE_TIMESTAMP_KEY);
      if (!timestamp) return false;
      const cacheAge = Date.now() - parseInt(timestamp, 10);
      return cacheAge < FEED_CACHE_EXPIRY_MS;
    } catch {
      return false;
    }
  }, [FEED_CACHE_EXPIRY_MS]);

  // Video preloading cache (defined before restoreFeedCache since it uses preloadVideos)
  const videoPreloadCache = useRef(new Map());

  // Preload videos for posts (non-blocking)
  const preloadVideos = useCallback((posts) => {
    if (!posts || !Array.isArray(posts)) return;
    posts.forEach(post => {
      if (!post.media || !Array.isArray(post.media)) return;

      post.media.forEach(mediaItem => {
        if (mediaItem.type === 'video' && mediaItem.url && !videoPreloadCache.current.has(mediaItem.url)) {
          videoPreloadCache.current.set(mediaItem.url, true);
          // Preload video in background (non-blocking)
          try {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.src = mediaItem.url;
            video.load();
          } catch (e) {
            console.warn('[Feed] Failed to preload video:', e);
          }
        }
      });
    });
  }, []);

  // Restore feed posts from cache
  const restoreFeedCache = useCallback(async (view) => {
    try {
      // First try to restore from all posts cache (faster, works across filters)
      const allCached = localStorage.getItem(ALL_POSTS_CACHE_KEY);
      if (allCached && isCacheValid()) {
        const allPosts = JSON.parse(allCached);
        const validPosts = allPosts.filter(post => isValidSupabasePost(post));

        if (validPosts.length > 0) {
          // Use cached posts immediately for instant display
          if (view === 'Masonry') {
            const filtered = sortPostsByTimestampDesc(filterOutHidden(validPosts));
            masonryPostIds.current.clear();
            filtered.forEach(post => masonryPostIds.current.add(post.id));
            setMasonryPosts(filtered);
            // Preload videos for cached posts (non-blocking)
            setTimeout(() => preloadVideos(filtered), 0);
          } else {
            const filtered = sortPostsByTimestampDesc(filterOutHidden(validPosts));
            postIds.current.clear();
            filtered.forEach(post => postIds.current.add(post.id));
            setFeedPosts(filtered);
            // Preload videos for cached posts (non-blocking)
            setTimeout(() => preloadVideos(filtered), 0);
          }

          // Validate in background (non-blocking)
          if (!isOffline()) {
            setTimeout(async () => {
              const postIdsToValidate = validPosts.map(post => post.id);
              const validPostIds = await validatePostsExist(postIdsToValidate);
              const existingPosts = validPosts.filter(post => validPostIds.has(post.id));

              if (existingPosts.length !== validPosts.length) {
                console.log(`[Feed] Background validation: filtered out ${validPosts.length - existingPosts.length} deleted posts`);
                const publicPosts = existingPosts.filter(post => {
                  const isPrivate = post?.is_private === true || post?.isPrivate === true;
                  return !isPrivate;
                });
                const filtered = sortPostsByTimestampDesc(filterOutHidden(publicPosts));
                localStorage.setItem(ALL_POSTS_CACHE_KEY, JSON.stringify(filtered.slice(0, 200)));
                localStorage.setItem(FEED_CACHE_TIMESTAMP_KEY, Date.now().toString());
              }
            }, 0);
          }

          return true;
        }
      }

      // Fallback to view-specific cache
      if (view === 'Masonry') {
        const cached = localStorage.getItem(MASONRY_CACHE_KEY);
        const cachedCursor = localStorage.getItem(MASONRY_CURSOR_KEY);
        if (cached) {
          const posts = JSON.parse(cached);
          const validPosts = posts.filter(post => isValidSupabasePost(post));

          if (validPosts.length > 0) {
            let existingPosts = validPosts;

            if (!isOffline()) {
              const postIdsToValidate = validPosts.map(post => post.id);
              const validPostIds = await validatePostsExist(postIdsToValidate);
              existingPosts = validPosts.filter(post => validPostIds.has(post.id));
            }

            const publicPosts = existingPosts.filter(post => {
              const isPrivate = post?.is_private === true || post?.isPrivate === true;
              return !isPrivate;
            });

            const filtered = sortPostsByTimestampDesc(filterOutHidden(publicPosts));
            masonryPostIds.current.clear();
            filtered.forEach(post => masonryPostIds.current.add(post.id));
            setMasonryPosts(filtered);
            setTimeout(() => preloadVideos(filtered), 0);
            if (cachedCursor) {
              setMasonryLastCursor(JSON.parse(cachedCursor));
            }
            return true;
          }
        }
      } else {
        const cached = localStorage.getItem(FEED_CACHE_KEY);
        const cachedCursor = localStorage.getItem(FEED_CURSOR_KEY);
        if (cached) {
          const posts = JSON.parse(cached);
          const validPosts = posts.filter(post => isValidSupabasePost(post));

          if (validPosts.length > 0) {
            let existingPosts = validPosts;

            if (!isOffline()) {
              const postIdsToValidate = validPosts.map(post => post.id);
              const validPostIds = await validatePostsExist(postIdsToValidate);
              existingPosts = validPosts.filter(post => validPostIds.has(post.id));
            }

            const publicPosts = existingPosts.filter(post => {
              const isPrivate = post?.is_private === true || post?.isPrivate === true;
              return !isPrivate;
            });

            const filtered = sortPostsByTimestampDesc(filterOutHidden(publicPosts));
            postIds.current.clear();
            filtered.forEach(post => postIds.current.add(post.id));
            setFeedPosts(filtered);
            setTimeout(() => preloadVideos(filtered), 0);
            if (cachedCursor) {
              setLastCursor(JSON.parse(cachedCursor));
            }
            return true;
          }
        }
      }
      return false;
    } catch (e) {
      console.warn('[Feed] Failed to restore cache:', e);
      if (e instanceof SyntaxError) {
        try {
          localStorage.removeItem(FEED_CACHE_KEY);
          localStorage.removeItem(MASONRY_CACHE_KEY);
          localStorage.removeItem(ALL_POSTS_CACHE_KEY);
          localStorage.removeItem(FEED_CACHE_TIMESTAMP_KEY);
        } catch (clearErr) {
          console.warn('[Feed] Failed to clear corrupted cache:', clearErr);
        }
      }
      return false;
    }
  }, [filterOutHidden, isCacheValid]);

  // Save scroll position
  const saveScrollPosition = useCallback(() => {
    try {
      if (feedContainerRef.current) {
        const scrollTop = feedContainerRef.current.scrollTop;
        localStorage.setItem(SCROLL_POSITION_KEY, scrollTop.toString());
      }
    } catch (e) {
      console.warn('[Feed] Failed to save scroll position:', e);
    }
  }, []);

  // Restore scroll position
  const restoreScrollPosition = useCallback(() => {
    try {
      const savedScroll = localStorage.getItem(SCROLL_POSITION_KEY);
      if (savedScroll && feedContainerRef.current && !scrollRestored.current) {
        const scrollTop = parseInt(savedScroll, 10);
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          if (feedContainerRef.current) {
            feedContainerRef.current.scrollTop = scrollTop;
            scrollRestored.current = true;
          }
        });
      }
    } catch (e) {
      console.warn('[Feed] Failed to restore scroll position:', e);
    }
  }, []);

  // Save scroll position on scroll (throttled)
  useEffect(() => {
    const container = feedContainerRef.current;
    if (!container) return;

    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        saveScrollPosition();
      }, 500); // Throttle to every 500ms
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [saveScrollPosition]);

  // Save cache when posts change
  useEffect(() => {
    if (!isInitialMount.current && feedPosts.length > 0 && selectedView !== 'Masonry') {
      saveFeedCache(feedPosts, lastCursor, selectedView);
    }
  }, [feedPosts, lastCursor, selectedView, saveFeedCache]);

  // Save masonry cache when posts change
  useEffect(() => {
    if (!isInitialMount.current && masonryPosts.length > 0 && selectedView === 'Masonry') {
      saveFeedCache(masonryPosts, masonryLastCursor, selectedView);
    }
  }, [masonryPosts, masonryLastCursor, selectedView, saveFeedCache]);

  // Restore cache on mount and when view changes
  useEffect(() => {
    scrollRestored.current = false; // Reset scroll restoration flag when view changes
    const restoreCache = async () => {
      const restored = await restoreFeedCache(selectedView);
      if (restored) {
        console.log(`[Feed] Restored ${selectedView} cache from localStorage`);
        // Restore scroll position after a brief delay to ensure DOM is ready
        setTimeout(() => {
          restoreScrollPosition();
        }, 100);
        if (isInitialMount.current) {
          isInitialMount.current = false;
        }
      } else if (isInitialMount.current) {
        // Only set initial mount to false if we didn't restore cache
        isInitialMount.current = false;
      }
    };
    restoreCache();
  }, [selectedView, restoreFeedCache, restoreScrollPosition]);

  // Save scroll position before leaving page
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveScrollPosition();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      saveScrollPosition(); // Also save when component unmounts
    };
  }, [saveScrollPosition]);

  // ---------- Stable, consistent pagination loader (Detailed/Grid) ----------
  const loadPosts = useCallback(async () => {
    if (loading || !hasMore || selectedView === 'Masonry') return;

    // Check if offline - if so, don't attempt to load new posts
    if (isOffline()) {
      console.log('[Feed] Offline detected - skipping post fetch, using cached posts');
      // Don't set hasMore to false - keep it true so we can retry when online
      return;
    }

    setLoading(true);
    try {
      console.log('[Feed] loading posts from Supabase...');

      let query = supabase
        .from('posts')
        .select('*')
        .eq('is_locked', false)
        .eq('is_private', false)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);

      // Add pagination if we have a cursor
      if (lastCursor?.ts && lastCursor?.id) {
        query = query.lt('created_at', lastCursor.ts);
      }

      const { data: posts, error } = await query;

      if (error) {
        console.error('[Feed] Supabase query error:', error);
        // On network error, don't clear existing posts or set hasMore to false
        // This allows retry when connection is restored
        if (error.message?.includes('fetch') || error.message?.includes('network') || error.code === 'PGRST116') {
          console.warn('[Feed] Network error - preserving existing posts, will retry when online');
          // Don't modify hasMore or clear posts - keep current state
          return;
        }
        // For other errors, stop trying to load more
        setHasMore(false);
        return;
      }

      if (!posts || posts.length === 0) {
        setHasMore(false);
        return;
      }

      // First, validate all fetched posts exist in database (batch validation)
      const postIdsToValidate = posts
        .filter(post => isValidSupabasePost(post))
        .map(post => post.id);

      const validPostIds = postIdsToValidate.length > 0
        ? await validatePostsExist(postIdsToValidate)
        : new Set();

      const toAdd = [];
      for (const post of posts) {
        // Validate post structure first
        if (!isValidSupabasePost(post)) continue;

        // Validate post exists in database (filter out deleted posts)
        if (!validPostIds.has(post.id)) {
          console.log(`[Feed] Skipping deleted post: ${post.id}`);
          continue;
        }

        if (postIds.current.has(post.id)) continue;
        if (hiddenPostIdsRef.current.has(post.id)) continue;
        if (blockedUserIdsRef.current.has(post.user_id)) continue;

        // Double-check: filter out locked or private posts (in case database filter didn't catch NULL values)
        const isLocked = post.is_locked === true || post.isLocked === true;
        const isPrivate = post.is_private === true || post.isPrivate === true;
        if (isLocked || isPrivate) continue;

        // Skip damaged video posts
        if (DAMAGED_VIDEO_POST_IDS.includes(post.id)) {
          continue;
        }

        // Check if post has media (media_names array) or caption or link
        const hasMedia = post.media_names && Array.isArray(post.media_names) && post.media_names.length > 0;
        const hasCaption = post.caption && post.caption.trim().length > 0;
        const hasLinkInCaption = post.caption && /https?:\/\/[^\s]+/.test(post.caption);

        // Include posts with media, caption, or links (links only for Detailed/Grid views)
        // Links are stored in the caption field
        if (hasMedia || hasCaption || hasLinkInCaption) {
          const cachedProfile = profileCacheRef.current.get(post.user_id);
          const fallbackUsername = `user_${post.user_id.slice(0, 8)}`;
          // Check if audio_filename exists and is not null/empty
          const hasAudioFilename = post.audio_filename != null &&
            post.audio_filename !== '' &&
            String(post.audio_filename).trim().length > 0;

          // Build media array from media_names
          const mediaArray = post.media_names ? post.media_names.map((filename, index) => ({
            type: filename.includes('.mp4') || filename.includes('.mov') ? 'video' : 'image',
            url: `https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/post-media/${post.user_id}/${filename}`,
            index
          })) : [];

          // Extract link from caption and add to media array
          if (hasLinkInCaption) {
            const linkMatch = post.caption.match(/https?:\/\/[^\s]+/);
            if (linkMatch && linkMatch[0]) {
              // Thumbnail is saved at {userId}/{postId}_thumb.jpg in post-media bucket
              // Note: thumbnailUrl may not exist, but we'll try it and fallback to defaults in the component
              const thumbnailUrl = `https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/post-media/${post.user_id}/${post.id}_thumb.jpg`;
              mediaArray.push({
                type: 'link',
                url: linkMatch[0],
                thumbnailUrl: thumbnailUrl // Use saved thumbnail from storage (if exists)
              });
              console.log(`[Feed] Added link post with link in caption: ${post.id}`, linkMatch[0]);
            }
          }

          // Double-check: ensure post is not private (safety check even though query filters it)
          if (isPrivate) {
            console.warn(`[Feed] Skipping private post that slipped through filter: ${post.id}`);
            continue;
          }

          // Transform the post immediately with cached data when available
          const transformedPost = {
            ...post,
            // Map Supabase fields to expected format
            postedByProfileImg: cachedProfile?.avatarUrl || getDefaultUserIcon(),
            postedByUsername: cachedProfile?.displayName || fallbackUsername,
            postedById: post.user_id,
            timestamp: post.created_at,
            media: mediaArray,
            audioFilename: hasAudioFilename ? String(post.audio_filename).trim() : null,
            audioUrl: hasAudioFilename
              ? `https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/post-media/${post.user_id}/${String(post.audio_filename).trim()}`
              : null,
            isLocked: isLocked,
            isPrivate: false, // Always false since we filter private posts
            isDeleted: false, // You might want to add this field to your posts table
            isSaved: savedPostIdsRef.current.has(post.id),
            _needsProfileUpdate: !cachedProfile // Only fetch profile if needed
          };

          toAdd.push(transformedPost);
          postIds.current.add(post.id);
        }
      }

      // Set cursor for next page
      const lastPost = posts[posts.length - 1];
      const newCursor = { ts: lastPost.created_at, id: lastPost.id };

      if (toAdd.length > 0) {
        // Preload videos for new posts (non-blocking)
        preloadVideos(toAdd);

        setFeedPosts(prev => {
          const updated = sortPostsByTimestampDesc([...prev, ...toAdd]); // ensure newest first
          // Save cache asynchronously
          setTimeout(() => {
            saveFeedCache(updated, newCursor, selectedView);
          }, 0);
          return updated;
        });
      }

      setLastCursor(newCursor);
      setHasMore(posts.length === PAGE_SIZE);

      console.log(`[Feed] loaded ${toAdd.length} posts from Supabase`);
    } catch (error) {
      console.error("[Feed] Error loading posts:", error);
      // On network error, preserve existing posts and allow retry
      if (error.message?.includes('fetch') || error.message?.includes('network') || error.name === 'TypeError') {
        console.warn('[Feed] Network error during post fetch - preserving existing posts');
        // Don't clear posts or modify hasMore - keep current state for retry
      }
      // For other errors, we still preserve existing posts (they're not cleared)
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, lastCursor, selectedView, saveFeedCache]);

  // Online/Offline event listeners - retry loading when connection is restored
  useEffect(() => {
    const handleOnline = () => {
      console.log('[Feed] Connection restored - retrying post load');
      // Retry loading posts if we don't have any or if we were trying to load more
      if (feedPosts.length === 0 && selectedView !== 'Masonry' && !loading) {
        loadPosts();
      } else if (hasMore && !loading && selectedView !== 'Masonry') {
        // If we have posts but were trying to load more, retry
        loadPosts();
      }
    };

    const handleOffline = () => {
      console.log('[Feed] Connection lost - using cached posts');
      // Posts are already preserved, just log the status
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [feedPosts.length, selectedView, loading, hasMore, loadPosts]);

  // Initialize and load posts - only if cache wasn't restored
  useEffect(() => {
    // Only load if we don't have cached posts and we're online
    if (feedPosts.length === 0 && selectedView !== 'Masonry' && !isOffline()) {
      const init = async () => {
        try {
          console.log('[Feed] Initializing...');
          await loadPosts();
        } catch (err) {
          console.error('[Feed] Init error:', err);
        }
      };
      init();
    } else if (feedPosts.length === 0 && selectedView !== 'Masonry' && isOffline()) {
      console.log('[Feed] Offline on init - using cached posts only');
    }
  }, [loadPosts, feedPosts.length, selectedView]);

  // Dedicated masonry loading function - loads into buffer
  const loadMasonryPosts = useCallback(async () => {
    if (masonryLoadingRef.current || !masonryHasMore) return;

    // Check if offline - if so, don't attempt to load new posts
    if (isOffline()) {
      console.log('[Feed] Offline detected - skipping masonry post fetch, using cached posts');
      // Don't set masonryHasMore to false - keep it true so we can retry when online
      return;
    }

    masonryLoadingRef.current = true;
    try {
      console.log('[Feed] loading masonry posts from Supabase...');

      let query = supabase
        .from('posts')
        .select('*')
        .eq('is_locked', false)
        .eq('is_private', false)
        .order('created_at', { ascending: false })
        .limit(30); // Load more posts to buffer

      if (masonryLastCursor?.ts && masonryLastCursor?.id) {
        query = query.lt('created_at', masonryLastCursor.ts);
      }

      const { data: posts, error } = await query;

      if (error) {
        console.error('[Feed] Supabase masonry query error:', error);
        // On network error, don't clear existing posts or set hasMore to false
        if (error.message?.includes('fetch') || error.message?.includes('network') || error.code === 'PGRST116') {
          console.warn('[Feed] Network error - preserving existing masonry posts, will retry when online');
          return;
        }
        // For other errors, stop trying to load more
        setMasonryHasMore(false);
        return;
      }

      if (!posts || posts.length === 0) {
        setMasonryHasMore(false);
        return;
      }

      // First, validate all fetched posts exist in database (batch validation)
      const postIdsToValidate = posts
        .filter(post => isValidSupabasePost(post))
        .map(post => post.id);

      const validPostIds = postIdsToValidate.length > 0
        ? await validatePostsExist(postIdsToValidate)
        : new Set();

      const newImagePosts = [];

      for (const post of posts) {
        // Validate post structure first
        if (!isValidSupabasePost(post)) continue;

        // Validate post exists in database (filter out deleted posts)
        if (!validPostIds.has(post.id)) {
          console.log(`[Feed] Skipping deleted masonry post: ${post.id}`);
          continue;
        }

        if (masonryPostIds.current.has(post.id)) continue;
        if (hiddenPostIdsRef.current.has(post.id)) continue;
        if (blockedUserIdsRef.current.has(post.user_id)) continue;

        // Double-check: filter out locked or private posts (in case database filter didn't catch NULL values)
        const isLocked = post.is_locked === true || post.isLocked === true;
        const isPrivate = post.is_private === true || post.isPrivate === true;
        if (isLocked || isPrivate) continue;

        // Skip damaged video posts
        if (DAMAGED_VIDEO_POST_IDS.includes(post.id)) continue;

        // Check if post contains links (skip these posts)
        const hasLinkInCaption = post.caption && /https?:\/\/[^\s]+/.test(post.caption);

        // Skip posts that contain links (masonry only shows images)
        if (hasLinkInCaption) {
          continue;
        }

        // Only get image posts for masonry
        if (post.media_names && Array.isArray(post.media_names) && post.media_names.length > 0) {
          // Check if any media is an image (not video)
          const hasImages = post.media_names.some(filename =>
            !filename.includes('.mp4') && !filename.includes('.mov') &&
            (filename.includes('.jpg') || filename.includes('.jpeg') || filename.includes('.png') || filename.includes('.webp'))
          );

          if (hasImages) {
            // Double-check: ensure post is not private (safety check even though query filters it)
            if (isPrivate) {
              console.warn(`[Feed] Skipping private masonry post that slipped through filter: ${post.id}`);
              continue;
            }

            // Transform the post immediately with placeholder data for faster loading
            const cachedProfile = profileCacheRef.current.get(post.user_id);
            const fallbackUsername = `user_${post.user_id.slice(0, 8)}`;
            // Check if audio_filename exists and is not null/empty
            const hasAudioFilename = post.audio_filename != null &&
              post.audio_filename !== '' &&
              String(post.audio_filename).trim().length > 0;

            const transformedPost = {
              ...post,
              postedByProfileImg: cachedProfile?.avatarUrl || getDefaultUserIcon(),
              postedByUsername: cachedProfile?.displayName || fallbackUsername,
              postedById: post.user_id,
              timestamp: post.created_at,
              media: post.media_names.map((filename, index) => ({
                type: filename.includes('.mp4') || filename.includes('.mov') ? 'video' : 'image',
                url: `https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/post-media/${post.user_id}/${filename}`,
                index
              })),
              audioFilename: hasAudioFilename ? String(post.audio_filename).trim() : null,
              audioUrl: hasAudioFilename
                ? `https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/post-media/${post.user_id}/${String(post.audio_filename).trim()}`
                : null,
              isLocked: isLocked,
              isPrivate: false, // Always false since we filter private posts
              isDeleted: false,
              isSaved: savedPostIdsRef.current.has(post.id),
              _needsProfileUpdate: !cachedProfile // Flag to update profile later
            };

            newImagePosts.push(transformedPost);
            masonryPostIds.current.add(post.id);
          }
        }
      }

      if (newImagePosts.length > 0) {
        // Add to buffer instead of directly to display
        setMasonryBuffer(prev => [...prev, ...newImagePosts]);
        console.log(`📦 Buffered ${newImagePosts.length} new masonry posts from Supabase`);
      }

      const lastPost = posts[posts.length - 1];
      setMasonryLastCursor({ ts: lastPost.created_at, id: lastPost.id });
      setMasonryHasMore(posts.length === 30);

    } catch (error) {
      console.error("Error loading masonry posts:", error);
      // On network error, preserve existing posts and allow retry
      if (error.message?.includes('fetch') || error.message?.includes('network') || error.name === 'TypeError') {
        console.warn('[Feed] Network error during masonry post fetch - preserving existing posts');
        // Don't clear posts or modify hasMore - keep current state for retry
      }
      // For other errors, we still preserve existing posts (they're not cleared)
    } finally {
      masonryLoadingRef.current = false;
    }
  }, [masonryLastCursor, masonryHasMore]);

  // initial fetch for Detailed/Grid
  useEffect(() => { loadPosts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update user profiles for posts that need it (runs after posts are loaded)
  useEffect(() => {
    const updateUserProfiles = async () => {
      const postsNeedingUpdate = feedPosts.filter(post => post._needsProfileUpdate);
      if (postsNeedingUpdate.length === 0) return;

      console.log(`[Feed] Updating profiles for ${postsNeedingUpdate.length} posts`);

      const updatedPosts = await Promise.all(
        postsNeedingUpdate.map(async (post) => {
          try {
            const profileInfo = await fetchAndCacheProfile(post.user_id);
            return {
              ...post,
              postedByProfileImg: profileInfo?.avatarUrl || post.postedByProfileImg || getDefaultUserIcon(),
              postedByUsername: profileInfo?.displayName || post.postedByUsername || `user_${post.user_id.slice(0, 8)}`,
              _needsProfileUpdate: false
            };
          } catch (error) {
            console.error(`[Feed] Failed to fetch profile for user ${post.user_id}:`, error);
            return {
              ...post,
              _needsProfileUpdate: false
            };
          }
        })
      );

      // Update the posts with new profile data
      setFeedPosts(prev =>
        prev.map(post => {
          const updatedPost = updatedPosts.find(up => up.id === post.id);
          return updatedPost || post;
        })
      );
    };

    if (feedPosts.length > 0) {
      updateUserProfiles();
    }
  }, [feedPosts, fetchAndCacheProfile]); // Only run when new posts are added

  // Update user profiles for masonry posts that need it
  useEffect(() => {
    const updateMasonryUserProfiles = async () => {
      const postsNeedingUpdate = masonryPosts.filter(post => post._needsProfileUpdate);
      if (postsNeedingUpdate.length === 0) return;

      console.log(`[Feed] Updating masonry profiles for ${postsNeedingUpdate.length} posts`);

      const updatedPosts = await Promise.all(
        postsNeedingUpdate.map(async (post) => {
          try {
            const profileInfo = await fetchAndCacheProfile(post.user_id);
            return {
              ...post,
              postedByProfileImg: profileInfo?.avatarUrl || post.postedByProfileImg || getDefaultUserIcon(),
              postedByUsername: profileInfo?.displayName || post.postedByUsername || `user_${post.user_id.slice(0, 8)}`,
              _needsProfileUpdate: false
            };
          } catch (error) {
            console.error(`[Feed] Failed to fetch masonry profile for user ${post.user_id}:`, error);
            return {
              ...post,
              _needsProfileUpdate: false
            };
          }
        })
      );

      // Update the masonry posts with new profile data
      setMasonryPosts(prev =>
        prev.map(post => {
          const updatedPost = updatedPosts.find(up => up.id === post.id);
          return updatedPost || post;
        })
      );
    };

    if (masonryPosts.length > 0) {
      updateMasonryUserProfiles();
    }
  }, [masonryPosts, fetchAndCacheProfile]); // Only run when new masonry posts are added

  // Initial masonry load when switching to masonry view
  useEffect(() => {
    if (selectedView === 'Masonry' && masonryPosts.length === 0 && masonryBuffer.length === 0) {
      loadMasonryPosts();
    }
  }, [selectedView, loadMasonryPosts, masonryPosts.length, masonryBuffer.length]);

  // Ultra-smooth masonry display system - zero flashing
  useEffect(() => {
    if (selectedView === 'Masonry') {
      const displayInterval = setInterval(() => {
        // Pause updates when create panel is expanded or post is focused
        if (!isMinimized || focusedPost) {
          return; // Skip this update cycle
        }

        // Move posts from buffer to display with debounced RAF for ultra-smooth updates
        if (masonryBuffer.length > 0) {
          // Clear any pending update
          if (masonryUpdateTimeoutRef.current) {
            clearTimeout(masonryUpdateTimeoutRef.current);
          }

          // Debounce the update to prevent rapid-fire changes
          masonryUpdateTimeoutRef.current = setTimeout(() => {
            requestAnimationFrame(() => {
              const postsToAdd = Math.min(masonryCount, masonryBuffer.length); // Add 1 post per column max
              const newPosts = masonryBuffer.slice(0, postsToAdd);
              const remainingBuffer = masonryBuffer.slice(postsToAdd);

              // Batch state updates to prevent multiple re-renders
              if (newPosts.length > 0) {
                setMasonryPosts(prev => {
                  const updated = [...prev, ...newPosts];
                  return updated.slice(-100); // Keep smaller limit for better performance
                });
                setMasonryBuffer(remainingBuffer);
                console.log(`🎨 Debounced-RAF added ${newPosts.length} posts`);
              }
            });
          }, 100); // 100ms debounce
        }

        // Load more if buffer is running low (less frequent checks)
        if (masonryBuffer.length < masonryCount * 4 && masonryHasMore && !masonryLoadingRef.current) {
          loadMasonryPosts();
        }
      }, 6000); // Even slower interval - 6 seconds for ultra-smooth experience

      return () => {
        clearInterval(displayInterval);
        if (masonryUpdateTimeoutRef.current) {
          clearTimeout(masonryUpdateTimeoutRef.current);
        }
      };
    }
  }, [selectedView, masonryBuffer, masonryCount, masonryHasMore, loadMasonryPosts, isMinimized, focusedPost]);

  // Reset masonry state when switching views
  useEffect(() => {
    if (selectedView !== 'Masonry') {
      setMasonryPosts([]);
      setMasonryBuffer([]);
      setMasonryLastCursor(null);
      setMasonryHasMore(true);
      masonryPostIds.current.clear();
      masonryDisplayIndex.current = 0;
    }
  }, [selectedView]);

  // Cleanup function to remove invalid/deleted/private posts from state and cache
  // Made more stable to prevent unnecessary re-renders
  const cleanupInvalidPosts = useCallback(async () => {
    try {
      // Use refs to get current values without causing re-renders
      const currentFeedPosts = feedPosts;
      const currentMasonryPosts = masonryPosts;
      const currentMasonryBuffer = masonryBuffer;

      // Collect all post IDs from both feed and masonry
      const allPostIds = new Set();
      currentFeedPosts.forEach(post => {
        if (post?.id) allPostIds.add(post.id);
      });
      currentMasonryPosts.forEach(post => {
        if (post?.id) allPostIds.add(post.id);
      });
      currentMasonryBuffer.forEach(post => {
        if (post?.id) allPostIds.add(post.id);
      });

      if (allPostIds.size === 0) return;

      // Validate posts exist AND are public in database (batch check)
      const validPostIds = await validatePostsExist(allPostIds);

      // Also check cached posts for private flag (in case they were turned private after caching)
      const privatePostIds = new Set();
      [...currentFeedPosts, ...currentMasonryPosts, ...currentMasonryBuffer].forEach(post => {
        if (post?.id) {
          const isPrivate = post?.is_private === true || post?.isPrivate === true;
          if (isPrivate) {
            privatePostIds.add(post.id);
          }
        }
      });

      // Find deleted/private post IDs
      const invalidPostIds = Array.from(allPostIds).filter(id =>
        !validPostIds.has(id) || privatePostIds.has(id)
      );

      if (invalidPostIds.length > 0) {
        const deletedCount = Array.from(allPostIds).filter(id => !validPostIds.has(id)).length;
        const privateCount = privatePostIds.size;
        console.log(`[Feed] Removing ${invalidPostIds.length} invalid posts from feed (${deletedCount} deleted, ${privateCount} private)`);

        // Remove from state - batch updates to minimize re-renders
        setFeedPosts(prev => {
          const filtered = prev.filter(post => !invalidPostIds.includes(post.id));
          if (filtered.length !== prev.length) {
            // Update postIds ref
            invalidPostIds.forEach(id => postIds.current.delete(id));
            // Save cleaned cache asynchronously
            setTimeout(() => {
              saveFeedCache(filtered, lastCursor, selectedView);
            }, 0);
          }
          return filtered;
        });

        setMasonryPosts(prev => {
          const filtered = prev.filter(post => !invalidPostIds.includes(post.id));
          if (filtered.length !== prev.length) {
            invalidPostIds.forEach(id => masonryPostIds.current.delete(id));
            setTimeout(() => {
              saveFeedCache(filtered, masonryLastCursor, 'Masonry');
            }, 0);
          }
          return filtered;
        });

        setMasonryBuffer(prev => {
          return prev.filter(post => !invalidPostIds.includes(post.id));
        });

        // Remove from focused post if it was deleted or turned private
        if (focusedPost && invalidPostIds.includes(focusedPost.id)) {
          setFocusedPost(null);
          setShowPostMenu(false);
          document.body.style.overflow = 'auto';
        }
      }
    } catch (err) {
      console.error('[Feed] Error cleaning up invalid posts:', err);
    }
  }, [feedPosts, masonryPosts, masonryBuffer, lastCursor, masonryLastCursor, selectedView, saveFeedCache, focusedPost]);

  // Run cleanup only on mount (removed periodic cleanup to prevent random refreshes)
  useEffect(() => {
    // Run cleanup once on mount to remove any invalid posts from cache
    cleanupInvalidPosts();
    // Removed periodic cleanup interval - user can manually refresh if needed
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sentinel intersection (Detailed/Grid only) - triggers when user scrolls near bottom
  useEffect(() => {
    if (!sentinelRef.current || selectedView === 'Masonry') return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading) {
          console.log('Sentinel triggered - loading more posts...');
          loadPosts();
        }
      },
      {
        root: feedContainerRef.current ?? null,
        rootMargin: '0px 0px 1000px 0px', // Trigger earlier for smoother experience
        threshold: 0.01
      }
    );

    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [hasMore, loading, selectedView, loadPosts]);

  // Remove top-up logic that interferes with natural infinite scroll


  // Focused post handlers
  const handlePostFocus = React.useCallback((post) => {
    // Helpful debug log so you can inspect any post you click
    try {
      console.groupCollapsed(`[Feed] Post clicked: ${post?.id || 'no-id'}`);
      console.log('Raw post object:', post);
      console.log('Identifiers:', {
        id: post?.id,
        user_id: post?.user_id,
        postedById: post?.postedById,
        postedByID: post?.postedByID,
        userId: post?.userId
      });
      console.log('Content fields:', {
        caption: post?.caption,
        media: post?.media,
        compressedMedia: post?.compressedMedia,
        media_names: post?.media_names,
        audio_filename: post?.audio_filename,
        audioUrl: post?.audioUrl
      });
      console.groupEnd();
    } catch (e) {
      console.error('[Feed] Failed to log clicked post details:', e);
    }

    // Track view when post is focused (clicked to view)
    if (post?.id && currentUser?.id) {
      (async () => {
        try {
          await trackPostViewRPC(post.id, currentUser.id);
        } catch {
          await trackPostView(post.id, currentUser.id);
        }
      })();
    }

    const allVideos = document.querySelectorAll('video');
    allVideos.forEach(video => { video.pause(); video.currentTime = 0; });
    setHideConfirmPost(null);
    setReportPost(null);
    setSelectedReportReason(null);
    setReportDescription("");
    setReportError("");
    setReportSubmitting(false);
    setFocusedPost({
      ...post,
      isSaved: savedPostIdsRef.current.has(post.id)
    });

    // Set initial index to first non-link item if available
    const allItems = Array.isArray(post.media) && post.media.length
      ? post.media
      : Array.isArray(post.compressedMedia)
        ? post.compressedMedia
        : [];
    const processedItems = allItems
      .map(r => {
        if (typeof r === "string") {
          try {
            const parsed = JSON.parse(r);
            if (parsed && parsed.type && parsed.url) return parsed;
          } catch {
            return { type: "link", url: r };
          }
        }
        if (r && r.type && r.url) return r;
        return null;
      })
      .filter(Boolean);

    // Find first non-link item index
    const firstNonLinkIndex = processedItems.findIndex(item => item?.type !== "link");
    setFocusedPostMediaIndex(firstNonLinkIndex !== -1 ? firstNonLinkIndex : 0);

    setShowPostMenu(false);
    document.body.style.overflow = 'hidden';
  }, [currentUser?.id]);
  const handleCloseFocusedPost = useCallback(() => {
    const focusedVideos = document.querySelectorAll('.focused-post-media video');
    focusedVideos.forEach(video => { video.pause(); video.currentTime = 0; });
    try {
      if (focusedAudioRef.current) {
        focusedAudioRef.current.pause();
        focusedAudioRef.current.currentTime = 0;
      }
    } catch (_) { }
    setIsFocusedAudioPlaying(false);
    setFocusedPost(null);
    setShowPostMenu(false);
    setHideConfirmPost(null);
    setReportPost(null);
    setSelectedReportReason(null);
    setReportDescription("");
    setReportError("");
    setReportSubmitting(false);
    document.body.style.overflow = 'auto';
  }, []);

  const hidePostById = useCallback(async (postId) => {
    if (!postId) return;

    // Optimistic UI update
    setHiddenPostIds(prev => {
      const next = new Set(prev);
      next.add(postId);
      hiddenPostIdsRef.current = next;
      // Also update localStorage to keep it in sync locally
      if (typeof window !== 'undefined') {
        localStorage.setItem(HIDDEN_POSTS_KEY, JSON.stringify(Array.from(next)));
      }
      return next;
    });

    setFeedPosts(prev => prev.filter(post => post.id !== postId));
    setMasonryPosts(prev => prev.filter(post => post.id !== postId));
    setMasonryBuffer(prev => prev.filter(post => post.id !== postId));

    postIds.current.delete(postId);
    masonryPostIds.current.delete(postId);

    if (focusedPost?.id === postId) {
      handleCloseFocusedPost();
    }

    // Persist to Supabase
    if (currentUser?.id) {
      try {
        const { error } = await supabase.from('hidden_posts').insert({
          user_id: currentUser.id,
          post_id: postId
        });
        if (error) {
          // Ignore duplicate key error (if already hidden)
          if (error.code !== '23505') throw error;
        }
      } catch (err) {
        console.error('[Feed] Failed to hide post in database:', err);
      }
    }
  }, [focusedPost?.id, handleCloseFocusedPost, currentUser?.id]);

  // Automatically hide posts whose media fails to load (e.g. missing file in Supabase storage)
  const handleBrokenMediaPost = useCallback((postId) => {
    if (!postId) return;
    console.warn('[Feed] Detected broken media for post. Hiding post from feed:', postId);
    hidePostById(postId);
  }, [hidePostById]);

  const handleCancelReport = useCallback(() => {
    setReportPost(null);
    setSelectedReportReason(null);
    setReportDescription("");
    setReportError("");
    setReportSubmitting(false);
  }, []);

  const handleSubmitReport = useCallback(async () => {
    if (!reportPost || !selectedReportReason || reportSubmitting) return;

    if (!currentUser?.id) {
      setReportError(t?.reportRequiresLogin || 'Please sign in to report posts.');
      return;
    }

    setReportError("");
    setReportSubmitting(true);

    try {
      const payload = {
        reporter_id: currentUser.id,
        post_id: reportPost.id,
        reason: selectedReportReason,
        description: reportDescription.trim() ? reportDescription.trim() : null
      };

      const { error } = await supabase.from('reports').insert(payload);
      if (error) throw error;

      hidePostById(reportPost.id);
      handleCancelReport();
    } catch (error) {
      console.error('[Feed] Failed to submit report:', error);
      setReportError(t?.reportError || 'Unable to submit report right now.');
    } finally {
      setReportSubmitting(false);
    }
  }, [currentUser?.id, handleCancelReport, hidePostById, reportDescription, reportPost, reportSubmitting, selectedReportReason, t]);
  const handlePostMenuToggle = (e) => { e.stopPropagation(); setShowPostMenu(prev => !prev); };

  const handleMenuAction = (action) => {
    switch (action) {
      case 'save':
        if (focusedPost) toggleSavePost(focusedPost);
        break;
      case 'goToPost':
        navigate(`/profile_posts/${focusedPost.postedById}?tab=posts&scrollTo=${focusedPost.id}`);
        handleCloseFocusedPost();
        break;
      case 'copyLink':
        sharePost(focusedPost);
        break;
      case 'hide':
        if (focusedPost) {
          setHideConfirmPost(focusedPost);
          setReportPost(null);
          setSelectedReportReason(null);
          setReportDescription("");
          setReportError("");
          setReportSubmitting(false);
        }
        break;
      case 'report':
        if (focusedPost) {
          setHideConfirmPost(null);
          setReportPost(focusedPost);
          setSelectedReportReason(null);
          setReportDescription("");
          setReportError("");
          setReportSubmitting(false);
        }
        break;
      case 'close':
        handleCloseFocusedPost();
        break;
      default:
        break;
    }
    setShowPostMenu(false);
  };
  const handleFullScreen = () => {
    if (!focusedPost) return;
    const mediaElement = document.querySelector('.focused-post-media img, .focused-post-media video');
    if (mediaElement) {
      if (mediaElement.requestFullscreen) mediaElement.requestFullscreen();
      else if (mediaElement.webkitRequestFullscreen) mediaElement.webkitRequestFullscreen();
      else if (mediaElement.msRequestFullscreen) mediaElement.msRequestFullscreen();
    }
  };
  const generateShareId = useCallback(() => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let id = '';
    for (let i = 0; i < 8; i += 1) {
      const idx = Math.floor(Math.random() * chars.length);
      id += chars[idx];
    }
    return id;
  }, []);

  const createShareLink = useCallback(async (post) => {
    if (!post?.id) throw new Error('Missing post data');
    if (!currentUser?.id) {
      alert('Please sign in to share this post.');
      return null;
    }

    const ownerId = post.postedById || post.postedByID || post.user_id || post.userId;
    const ownerUsername = post.postedByUsername || post.authorUsername || null;
    if (!ownerId || !ownerUsername) throw new Error('Missing post owner information');

    let lastError = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const shareId = generateShareId();
      const shareBase = process.env.REACT_APP_SHARE_BASE_URL;
      // SPA view URL (used within site and stored to DB)
      const viewUrl = (typeof window !== 'undefined')
        ? `${window.location.origin.replace(/\/$/, '')}/@${encodeURIComponent(ownerUsername)}/${post.id}/${shareId}/p`
        : `/@${encodeURIComponent(ownerUsername)}/${post.id}/${shareId}/p`;
      // External share URL (for social crawlers); fall back to view URL if base missing
      const shareUrl = shareBase
        ? `${shareBase.replace(/\/$/, '')}/@${encodeURIComponent(ownerUsername)}/${post.id}/${shareId}/p`
        : viewUrl;
      const payload = {
        post_id: post.id,
        share_id: shareId,
        share_url: viewUrl,
        sharer_id: currentUser.id
      };

      const { error } = await supabase.from('shares').insert(payload);
      if (!error) {
        return { shareUrl, viewUrl };
      }

      lastError = error;
      if (!error?.code || error.code !== '23505') {
        break;
      }
    }

    console.error('[Feed] Failed to create share record:', lastError);
    throw lastError || new Error('Unable to create share link');
  }, [currentUser?.id, generateShareId]);

  // Helper function to check if post is link-only (no media, no audio, only link in caption)
  const isLinkOnlyPost = useCallback((post) => {
    if (!post) return false;

    // Check if post has actual media (images/videos) - exclude links
    const hasActualMedia = (post.media_names && Array.isArray(post.media_names) && post.media_names.length > 0);

    // Check media array for non-link items (images/videos)
    const mediaList = Array.isArray(post.media) && post.media.length
      ? post.media
      : Array.isArray(post.compressedMedia)
        ? post.compressedMedia
        : [];

    const hasNonLinkMedia = mediaList.some(item => {
      if (typeof item === "string") {
        try {
          const parsed = JSON.parse(item);
          return parsed?.type && parsed.type !== "link";
        } catch {
          return false; // If it's a string and not JSON, it might be a link
        }
      }
      return item?.type && item.type !== "link";
    });

    // Check if post has audio
    const hasAudio = post.audio_filename != null &&
      post.audio_filename !== '' &&
      String(post.audio_filename).trim().length > 0;

    // Check if post has link in caption or media array
    const hasLinkInCaption = post.caption && /https?:\/\/[^\s]+/.test(post.caption);
    const hasLinkInMedia = mediaList.some(item => {
      if (typeof item === "string") {
        try {
          const parsed = JSON.parse(item);
          return parsed?.type === "link";
        } catch {
          return /https?:\/\/[^\s]+/.test(item);
        }
      }
      return item?.type === "link";
    });
    const hasLink = hasLinkInCaption || hasLinkInMedia;

    // Post is link-only if: has link AND no actual media (media_names) AND no non-link media items AND no audio
    return hasLink && !hasActualMedia && !hasNonLinkMedia && !hasAudio;
  }, []);

  // Helper function to extract link URL from post
  const extractLinkUrl = useCallback((post) => {
    if (!post) return null;

    // Check media array for link item
    const mediaList = Array.isArray(post.media) && post.media.length
      ? post.media
      : Array.isArray(post.compressedMedia)
        ? post.compressedMedia
        : [];

    const linkItem = mediaList.find(item => {
      if (typeof item === "string") {
        try {
          const parsed = JSON.parse(item);
          return parsed?.type === "link" && parsed?.url;
        } catch {
          return /https?:\/\/[^\s]+/.test(item);
        }
      }
      return item?.type === "link" && item?.url;
    });

    if (linkItem) {
      if (typeof linkItem === "string") {
        try {
          const parsed = JSON.parse(linkItem);
          return parsed?.url || linkItem;
        } catch {
          return linkItem;
        }
      }
      return linkItem.url;
    }

    // Fallback: extract from caption
    if (post.caption) {
      const match = post.caption.match(/https?:\/\/[^\s]+/);
      if (match && match[0]) {
        return match[0];
      }
    }

    return null;
  }, []);

  const sharePost = useCallback(async (post) => {
    try {
      // Check if post is link-only
      const isLinkOnly = isLinkOnlyPost(post);
      console.log('[Feed] Share - isLinkOnly:', isLinkOnly, {
        postId: post?.id,
        hasMediaNames: post?.media_names?.length > 0,
        hasMedia: post?.media?.length > 0,
        hasAudio: post?.audio_filename,
        hasLinkInCaption: post?.caption && /https?:\/\/[^\s]+/.test(post.caption)
      });

      if (isLinkOnly) {
        const linkUrl = extractLinkUrl(post);
        console.log('[Feed] Share - Extracted link URL:', linkUrl);
        if (linkUrl) {
          // Share the actual link URL directly
          if (navigator.share) {
            try {
              await navigator.share({
                title: post?.caption || 'Shared link',
                url: linkUrl
              });
            } catch (shareError) {
              console.warn('[Feed] navigator.share failed, falling back to clipboard', shareError);
              await navigator.clipboard.writeText(linkUrl);
              alert('Link copied to clipboard!');
            }
          } else {
            await navigator.clipboard.writeText(linkUrl);
            alert('Link copied to clipboard!');
          }
          return;
        } else {
          console.warn('[Feed] Share - Link-only post but could not extract URL');
        }
      }

      // For regular posts, create share link as before
      const result = await createShareLink(post);
      if (!result) return;
      const shareUrl = typeof result === 'string' ? result : result.shareUrl || result.viewUrl;
      const title = `${post?.postedByUsername || 'iWinto'}'s post`;

      if (navigator.share) {
        try {
          await navigator.share({ title, url: shareUrl });
        } catch (shareError) {
          console.warn('[Feed] navigator.share failed, falling back to clipboard', shareError);
          await navigator.clipboard.writeText(shareUrl);
          alert('Link copied to clipboard!');
        }
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('[Feed] Unable to share post:', error);
      alert('Unable to share this post right now.');
    }
  }, [createShareLink, isLinkOnlyPost, extractLinkUrl]);

  const handleShare = useCallback(() => {
    if (focusedPost) {
      sharePost(focusedPost);
    }
  }, [focusedPost, sharePost]);

  const handleFocusedAudioPlay = useCallback((e) => {
    e.stopPropagation();
    if (!focusedAudioRef.current || !focusedAudioUrl) return;

    // Pause all other audios
    const allAudios = document.querySelectorAll('.feed-post-audio');
    allAudios.forEach(audio => {
      if (audio !== focusedAudioRef.current) {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch (_) { }
      }
    });

    // Pause all videos (grid + focused)
    const allVideos = document.querySelectorAll('video');
    allVideos.forEach(video => {
      try {
        video.pause();
        video.currentTime = 0;
      } catch (_) { }
    });

    if (isFocusedAudioPlaying) {
      try {
        focusedAudioRef.current.pause();
      } catch (_) { }
      setIsFocusedAudioPlaying(false);
    } else {
      try {
        focusedAudioRef.current.play();
      } catch (_) { }
      setIsFocusedAudioPlaying(true);
    }
  }, [isFocusedAudioPlaying, focusedAudioUrl]);

  useEffect(() => {
    const audio = focusedAudioRef.current;
    if (!audio || !focusedAudioUrl) return;

    const handlePlay = () => setIsFocusedAudioPlaying(true);
    const handlePause = () => setIsFocusedAudioPlaying(false);
    const handleEnded = () => setIsFocusedAudioPlaying(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [focusedAudioUrl]);
  const handleLike = () => {
    if (!focusedPost) return;
    toggleLikePost(focusedPost.id);
  };

  const handleGridLikeClick = React.useCallback((e, post) => {
    e.stopPropagation();
    toggleLikePost(post?.id);
  }, [toggleLikePost]);
  const handleGridShareClick = React.useCallback((e, post) => {
    e.stopPropagation();
    sharePost(post);
  }, [sharePost]);

  const getFocusedPostMediaItems = useCallback(() => {
    if (!focusedPost) return [];
    const rawList = Array.isArray(focusedPost.media) && focusedPost.media.length
      ? focusedPost.media
      : Array.isArray(focusedPost.compressedMedia)
        ? focusedPost.compressedMedia
        : [];
    let items = rawList
      .map(r => {
        if (typeof r === "string") {
          try {
            const parsed = JSON.parse(r);
            if (parsed && parsed.type && parsed.url) return parsed;
          } catch {
            return { type: "link", url: r };
          }
        }
        if (r && r.type && r.url) return r;
        return null;
      })
      .filter(Boolean);
    if (items.length === 0 && focusedPost.caption) {
      const match = focusedPost.caption.match(/https?:\/\/[^\s]+/);
      if (match) items = [{ type: "link", url: match[0] }];
    }
    return items;
  }, [focusedPost]);

  const handleFocusedPostPrev = useCallback((e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    if (!focusedPost) return;
    // Pause all videos (focused and feed videos)
    const allVideos = document.querySelectorAll('video');
    allVideos.forEach(video => { video.pause(); video.currentTime = 0; });

    // Use functional update to ensure we have the latest state and latest media items
    setFocusedPostMediaIndex(currentIndex => {
      const allItems = getFocusedPostMediaItems();
      if (allItems.length <= 1) return currentIndex;
      // Simple sequential navigation: go to previous item, wrap around to last if at first
      return currentIndex === 0 ? allItems.length - 1 : currentIndex - 1;
    });
  }, [focusedPost, getFocusedPostMediaItems]);

  const handleFocusedPostNext = useCallback((e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    if (!focusedPost) return;
    // Pause all videos (focused and feed videos)
    const allVideos = document.querySelectorAll('video');
    allVideos.forEach(video => { video.pause(); video.currentTime = 0; });

    // Use functional update to ensure we have the latest state and latest media items
    setFocusedPostMediaIndex(currentIndex => {
      const allItems = getFocusedPostMediaItems();
      if (allItems.length <= 1) return currentIndex;
      // Simple sequential navigation: go to next item, wrap around to first if at last
      return currentIndex === allItems.length - 1 ? 0 : currentIndex + 1;
    });
  }, [focusedPost, getFocusedPostMediaItems]);

  // Helper function to get only non-link media items (for counter and navigation)
  const getFocusedPostNonLinkMediaItems = () => {
    const allItems = getFocusedPostMediaItems();
    return allItems.filter(item => item?.type !== "link");
  };

  useEffect(() => {
    return () => { document.body.style.overflow = 'auto'; };
  }, []);

  // Helper function to filter posts based on filterType
  const filterPostsByType = useCallback((posts) => {
    return posts.filter(post => {
      if (filterType === "Locked") return post.isLocked;
      if (filterType === "All") {
        // All filter: only images and videos (no links)
        if (post.isLocked) return false;
        const mediaList = Array.isArray(post.media) && post.media.length
          ? post.media
          : Array.isArray(post.compressedMedia)
            ? post.compressedMedia
            : [];
        const items = mediaList.map(item => {
          if (typeof item === "string") {
            try { return JSON.parse(item); } catch { return { type: "link" }; }
          }
          return item;
        });
        // Exclude posts that only have links
        const hasOnlyLinks = items.length > 0 && items.every(item => item?.type === "link");
        const hasLinkInCaption = post.caption?.match(/https?:\/\/[^\s]+/);
        if (hasOnlyLinks || (items.length === 0 && hasLinkInCaption)) return false;
        return true;
      }
      if (post.isLocked) return false;

      const mediaList = Array.isArray(post.media) && post.media.length
        ? post.media
        : Array.isArray(post.compressedMedia)
          ? post.compressedMedia
          : [];

      const items = mediaList.map(item => {
        if (typeof item === "string") {
          try { return JSON.parse(item); } catch { return { type: "link" }; }
        }
        return item;
      });

      if (filterType === "Links") {
        return items.some(item => item?.type === "link") || post.caption?.match(/https?:\/\/[^\s]+/);
      }
      if (filterType === "Videos") return items.some(item => item?.type === "video");
      if (filterType === "Audio") return items.some(item => item?.type === "audio");
      if (filterType === "Image") return items.some(item => item?.type === "image");
      return true;
    });
  }, [filterType]);

  // Get all posts in order based on selected view, filtered by filterType
  const getAllPosts = useCallback(() => {
    const allPosts = selectedView === 'Masonry' ? masonryPosts : feedPosts;
    return filterPostsByType(allPosts);
  }, [selectedView, feedPosts, masonryPosts, filterPostsByType]);

  // Navigate to next post (Up arrow - like scrolling up in reels)
  const handleNavigateToNextPost = useCallback(() => {
    if (!focusedPost) return;

    const allPosts = getAllPosts();
    const currentIndex = allPosts.findIndex(post => post.id === focusedPost.id);

    if (currentIndex === -1) return;

    // Down arrow goes to next post (higher index)
    const nextIndex = currentIndex + 1;
    if (nextIndex < allPosts.length) {
      const nextPost = allPosts[nextIndex];
      // Pause all videos
      const allVideos = document.querySelectorAll('video');
      allVideos.forEach(video => { video.pause(); video.currentTime = 0; });

      // Track view
      if (currentUser?.id) {
        (async () => {
          try {
            await trackPostViewRPC(nextPost.id, currentUser.id);
          } catch {
            await trackPostView(nextPost.id, currentUser.id);
          }
        })();
      }

      // Set focused post
      setFocusedPost({
        ...nextPost,
        isSaved: savedPostIdsRef.current.has(nextPost.id)
      });

      // Set initial media index
      const allItems = Array.isArray(nextPost.media) && nextPost.media.length
        ? nextPost.media
        : Array.isArray(nextPost.compressedMedia)
          ? nextPost.compressedMedia
          : [];
      const processedItems = allItems
        .map(r => {
          if (typeof r === "string") {
            try {
              const parsed = JSON.parse(r);
              if (parsed && parsed.type && parsed.url) return parsed;
            } catch {
              return { type: "link", url: r };
            }
          }
          if (r && r.type && r.url) return r;
          return null;
        })
        .filter(Boolean);

      const firstNonLinkIndex = processedItems.findIndex(item => item?.type !== "link");
      setFocusedPostMediaIndex(firstNonLinkIndex !== -1 ? firstNonLinkIndex : 0);
    }
  }, [focusedPost, getAllPosts, currentUser?.id]);

  // Navigate to previous post (Up arrow - like scrolling up in reels)
  const handleNavigateToPrevPost = useCallback(() => {
    if (!focusedPost) return;

    const allPosts = getAllPosts();
    const currentIndex = allPosts.findIndex(post => post.id === focusedPost.id);

    if (currentIndex === -1) return;

    // Up arrow goes to previous post (lower index)
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      const prevPost = allPosts[prevIndex];
      // Pause all videos
      const allVideos = document.querySelectorAll('video');
      allVideos.forEach(video => { video.pause(); video.currentTime = 0; });

      // Track view
      if (currentUser?.id) {
        (async () => {
          try {
            await trackPostViewRPC(prevPost.id, currentUser.id);
          } catch {
            await trackPostView(prevPost.id, currentUser.id);
          }
        })();
      }

      // Set focused post
      setFocusedPost({
        ...prevPost,
        isSaved: savedPostIdsRef.current.has(prevPost.id)
      });

      // Set initial media index
      const allItems = Array.isArray(prevPost.media) && prevPost.media.length
        ? prevPost.media
        : Array.isArray(prevPost.compressedMedia)
          ? prevPost.compressedMedia
          : [];
      const processedItems = allItems
        .map(r => {
          if (typeof r === "string") {
            try {
              const parsed = JSON.parse(r);
              if (parsed && parsed.type && parsed.url) return parsed;
            } catch {
              return { type: "link", url: r };
            }
          }
          if (r && r.type && r.url) return r;
          return null;
        })
        .filter(Boolean);

      const firstNonLinkIndex = processedItems.findIndex(item => item?.type !== "link");
      setFocusedPostMediaIndex(firstNonLinkIndex !== -1 ? firstNonLinkIndex : 0);
    }
  }, [focusedPost, getAllPosts, currentUser?.id]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!focusedPost) {
        // Only handle Escape when no post is focused
        if (e.key === 'Escape') return;
        return;
      }

      // Don't handle keyboard navigation if user is typing in an input/textarea/contenteditable
      const target = e.target;
      const isInputElement = target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('input, textarea, [contenteditable="true"]');

      if (isInputElement && e.key !== 'Escape') {
        return; // Allow normal input behavior, but still allow Escape to close
      }

      // Prevent default behavior for arrow keys when post is focused
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Escape key - close focused post
      if (e.key === 'Escape') {
        handleCloseFocusedPost();
        return;
      }

      // Left/Right arrows - navigate carousel
      if (e.key === 'ArrowLeft') {
        handleFocusedPostPrev(e);
        return;
      }
      if (e.key === 'ArrowRight') {
        handleFocusedPostNext(e);
        return;
      }

      // Up/Down arrows - navigate between posts
      if (e.key === 'ArrowDown') {
        handleNavigateToNextPost();
        return;
      }
      if (e.key === 'ArrowUp') {
        handleNavigateToPrevPost();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [focusedPost, handleCloseFocusedPost, handleNavigateToNextPost, handleNavigateToPrevPost, handleFocusedPostPrev, handleFocusedPostNext]);

  // Mobile detection effect - update isMobileRef on resize
  useEffect(() => {
    const checkMobile = () => {
      isMobileRef.current = window.innerWidth <= 768;
    };
    checkMobile();
    window.addEventListener('resize', checkMobile, { passive: true });
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Touch swipe handlers for mobile navigation in focused post overlay
  const handleTouchStart = useCallback((e) => {
    if (!isMobileRef.current || !focusedPost) return;
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchEndRef.current = { x: touch.clientX, y: touch.clientY };
  }, [focusedPost]);

  const handleTouchMove = useCallback((e) => {
    if (!isMobileRef.current || !focusedPost) return;
    // Prevent the background/overlay from scrolling while swiping
    if (e.cancelable) e.preventDefault();
    const touch = e.touches[0];
    touchEndRef.current = { x: touch.clientX, y: touch.clientY };
  }, [focusedPost]);

  const handleTouchEnd = useCallback(() => {
    if (!isMobileRef.current || !focusedPost) return;

    const SWIPE_THRESHOLD = 50; // Minimum distance for swipe detection
    const deltaX = touchEndRef.current.x - touchStartRef.current.x;
    const deltaY = touchEndRef.current.y - touchStartRef.current.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    // Reset touch refs
    touchStartRef.current = { x: 0, y: 0 };
    touchEndRef.current = { x: 0, y: 0 };

    // Check if either direction passes threshold
    if (absX < SWIPE_THRESHOLD && absY < SWIPE_THRESHOLD) return;

    // Determine primary swipe direction
    if (absY > absX) {
      // Vertical swipe - navigate between posts (like reels)
      if (deltaY < -SWIPE_THRESHOLD) {
        // Swipe up = go to next post (scroll down in reels)
        handleNavigateToNextPost();
      } else if (deltaY > SWIPE_THRESHOLD) {
        // Swipe down = go to previous post (scroll up in reels)
        handleNavigateToPrevPost();
      }
    } else {
      // Horizontal swipe - navigate media carousel
      if (deltaX < -SWIPE_THRESHOLD) {
        // Swipe left = next media
        handleFocusedPostNext();
      } else if (deltaX > SWIPE_THRESHOLD) {
        // Swipe right = previous media
        handleFocusedPostPrev();
      }
    }
  }, [focusedPost, handleNavigateToNextPost, handleNavigateToPrevPost, handleFocusedPostNext, handleFocusedPostPrev]);

  // ---------- render ----------

  return (
    <>
      {hideConfirmPost && (
        <div className="feed-hide-overlay" onClick={() => setHideConfirmPost(null)}>
          <div className="feed-hide-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="feed-hide-icon">
              <FaEyeSlash />
            </div>
            <h3 className="feed-hide-title">{t.feed?.hideModal?.title || "Hide this post?"}</h3>
            <p className="feed-hide-subtitle">
              {t.feed?.hideModal?.subtitle || "Are you sure you want to hide this post? It will no longer appear in your feed."}
            </p>
            <div className="feed-hide-actions">
              <button
                type="button"
                className="feed-hide-button feed-hide-secondary"
                onClick={() => setHideConfirmPost(null)}
              >
                {t.feed?.hideModal?.cancel || "Cancel"}
              </button>
              <button
                type="button"
                className="feed-hide-button feed-hide-primary"
                onClick={() => {
                  if (hideConfirmPost?.id) {
                    hidePostById(hideConfirmPost.id);
                  }
                  setHideConfirmPost(null);
                  setReportPost(null);
                  setSelectedReportReason(null);
                  setReportDescription("");
                  setReportError("");
                  setReportSubmitting(false);
                }}
              >
                {t.feed?.hideModal?.confirm || "Hide post"}
              </button>
            </div>
          </div>
        </div>
      )}

      {reportPost && (
        <div className="feed-report-overlay" onClick={handleCancelReport}>
          <div className="feed-report-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="feed-report-header">
              <div className="feed-report-icon">
                <FaFlag />
              </div>
              <h3 className="feed-report-title">{t.feed?.reportModal?.title || "Report Post"}</h3>
              <p className="feed-report-subtitle">
                {t.feed?.reportModal?.subtitle || "Why are you reporting this post?"}
              </p>
            </div>
            <div className="feed-report-options">
              {REPORT_REASONS.map((reason) => (
                <button
                  type="button"
                  key={reason.value}
                  className={`feed-report-option ${selectedReportReason === reason.value ? "active" : ""}`}
                  onClick={() => setSelectedReportReason(reason.value)}
                >
                  <span className="feed-report-option-title">
                    {t.feed?.reportModal?.reasons?.[reason.value]?.title || reason.label}
                  </span>
                  <span className="feed-report-option-desc">
                    {t.feed?.reportModal?.reasons?.[reason.value]?.description || reason.description}
                  </span>
                </button>
              ))}
            </div>
            <textarea
              className="feed-report-textarea"
              placeholder={t.feed?.reportModal?.placeholder || "Add more details (optional)"}
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              rows={3}
            />
            {reportError && <div className="feed-report-error">{reportError}</div>}
            <div className="feed-report-actions">
              <button
                type="button"
                className="feed-report-button feed-report-secondary"
                onClick={handleCancelReport}
              >
                {t.feed?.reportModal?.cancel || "Cancel"}
              </button>
              <button
                type="button"
                className="feed-report-button feed-report-primary"
                onClick={handleSubmitReport}
                disabled={!selectedReportReason || reportSubmitting}
              >
                {reportSubmitting
                  ? t.feed?.reportModal?.submitting || "Submitting..."
                  : t.feed?.reportModal?.submit || "Submit report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Focused Post Overlay */}
      {focusedPost && (
        <div className="focused-post-overlay" onClick={handleCloseFocusedPost}>
          <div
            className="focused-post-container"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >

            {/* Header */}
            <div className="focused-post-header">
              <div
                className="focused-post-user-info"
                onClick={() => navigate(`/profile/${focusedPost.postedById}`)}
                style={{ cursor: 'pointer' }}
              >
                <img
                  src={focusedPost.postedByProfileImg}
                  alt={focusedPost.postedByUsername}
                  className="focused-post-avatar"
                />
                <span className="focused-post-username">{focusedPost.postedByUsername}</span>
              </div>
              <div className="focused-post-menu-wrapper">
                <button
                  className="focused-post-menu-button"
                  onClick={handlePostMenuToggle}
                >
                  <FaEllipsisV />
                </button>
                {showPostMenu && (
                  <div className="focused-post-menu">
                    <div className="menu-item" onClick={() => handleMenuAction('save')}>
                      <FaBookmark style={{ color: focusedPost?.isSaved ? '#3b82f6' : undefined }} />
                      <span>{focusedPost?.isSaved ? (t.feed?.menu?.unsave || "Unsave") : (t.feed?.menu?.save || "Save")}</span>
                    </div>
                    <div className="menu-item" onClick={() => handleMenuAction('goToPost')}>
                      <FaLink /><span>{t.feed?.menu?.goToPost || "Go to post"}</span>
                    </div>
                    <div className="menu-item" onClick={() => handleMenuAction('copyLink')}>
                      <FaLink /><span>{t.feed?.menu?.copyLink || "Copy link"}</span>
                    </div>
                    <div className="menu-item" onClick={() => handleMenuAction('hide')}>
                      <FaEyeSlash /><span>{t.feed?.menu?.hide || "Hide"}</span>
                    </div>
                    <div className="menu-item" onClick={() => handleMenuAction('report')}>
                      <FaFlag /><span>{t.feed?.menu?.report || "Report"}</span>
                    </div>
                    <div className="menu-item" onClick={() => handleMenuAction('close')}>
                      <FaTimes /><span>{t.feed?.menu?.close || "Close"}</span>
                    </div>
                  </div>
                )}

              </div>
            </div>

            {/* Media */}
            <div
              className="focused-post-media"
              onClick={() => {
                try {
                  const focusedVideos = document.querySelectorAll('.focused-post-media video');
                  focusedVideos.forEach(v => {
                    try { v.pause(); v.currentTime = 0; v.muted = true; } catch (_) { }
                  });
                } catch (_) { }
                navigate(`/profile_posts/${focusedPost.postedById}?tab=posts&scrollTo=${focusedPost.id}`);
              }}
              style={{ cursor: 'pointer' }}
            >
              {(() => {
                const mediaItems = getFocusedPostMediaItems();
                const current = mediaItems.length > 0 ? mediaItems[focusedPostMediaIndex] : null;
                if (!current) return null;

                switch (current.type) {
                  case "image":
                    return <img src={current.url} alt="" className="focused-post-image" />;
                  case "video":
                    return (
                      <video
                        key={`focused-video-${focusedPost.id}-${focusedPostMediaIndex}-${current.url}`}
                        controls
                        src={current.url}
                        className="focused-post-video"
                        preload="metadata"
                        playsInline
                        onLoadedMetadata={(e) => {
                          // Ensure video starts unmuted in focused mode
                          try {
                            e.target.muted = false;
                            e.target.volume = 1;
                          } catch (_) { }
                          e.target.currentTime = 0;
                          e.target.pause();
                        }}
                        onClick={(e) => {
                          // Prevent default toggle play when using the video area as a navigation click
                          e.preventDefault();
                          // Do not stop propagation so the parent click navigates
                          try {
                            e.currentTarget.pause();
                            e.currentTarget.currentTime = 0;
                          } catch (_) { }
                        }}
                      />
                    );
                  case "audio":
                    return (
                      <div className="focused-post-audio-container">
                        <audio controls src={current.url} className="focused-post-audio" />
                      </div>
                    );
                  case "link":
                    return (
                      <div className="focused-post-link-thumb">
                        <img
                          src={(() => {
                            // Priority: saved thumbnail > fetched thumbnail > default based on type
                            if (current.thumbnailUrl) return current.thumbnailUrl;

                            // Try to get thumbnail from URL
                            const thumbUrl = getThumbnailUrl(current.url);
                            if (thumbUrl && !thumbUrl.includes('default-')) return thumbUrl;

                            // Determine default based on URL
                            const url = (current.url || '').toLowerCase();
                            if (url.includes('youtube.com') || url.includes('youtu.be') || url.includes('/shorts/')) {
                              return "/default-youtubeshorts-thumb.jpeg";
                            } else if (url.includes('instagram.com')) {
                              return "/default-instagram-thumb.jpeg";
                            } else if (url.includes('tiktok.com')) {
                              return "/default-tiktok-thumb.jpeg";
                            } else if (url.includes('facebook.com')) {
                              return "/default-facebook-thumb2.png";
                            } else {
                              return "/default-link-placeholder.png";
                            }
                          })()}
                          alt="link thumbnail"
                          className="focused-post-link-thumb-img"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(current.url, "_blank");
                          }}
                          onError={(e) => {
                            // Prevent infinite loop
                            if (e.target.src.includes('default-')) return;

                            // Determine link type and use appropriate default
                            const url = (current.url || '').toLowerCase();
                            if (url.includes('youtube.com') || url.includes('youtu.be') || url.includes('/shorts/')) {
                              e.target.src = "/default-youtubeshorts-thumb.jpeg";
                            } else if (url.includes('instagram.com')) {
                              e.target.src = "/default-instagram-thumb.jpeg";
                            } else if (url.includes('tiktok.com')) {
                              e.target.src = "/default-tiktok-thumb.jpeg";
                            } else if (url.includes('facebook.com')) {
                              e.target.src = "/default-facebook-thumb2.png";
                            } else {
                              e.target.src = "/default-link-placeholder.png";
                            }
                          }}
                        />
                      </div>
                    );
                  default:
                    return null;
                }
              })()}

              {/* Focused audio play button for posts with attached audio (audio_filename / audioUrl) */}
              {focusedAudioUrl && (
                <div className="grid-audio-button-container focused-post-audio-button-container">
                  <button
                    className="grid-action-button grid-audio-button"
                    onClick={handleFocusedAudioPlay}
                  >
                    {isFocusedAudioPlaying ? <FaPause /> : <FaPlay />}
                  </button>
                  {/* Hidden audio element for playback */}
                  <audio
                    ref={focusedAudioRef}
                    src={focusedAudioUrl}
                    className="feed-post-audio"
                    preload="none"
                  />
                </div>
              )}

              {/* Carousel Navigation Arrows */}
              {(() => {
                const allItems = getFocusedPostMediaItems();

                // Show arrows if there are multiple items
                if (allItems.length > 1) {
                  return (
                    <>
                      <button
                        className="focused-post-carousel-arrow left"
                        onClick={handleFocusedPostPrev}
                      >
                        ‹
                      </button>
                      <button
                        className="focused-post-carousel-arrow right"
                        onClick={handleFocusedPostNext}
                      >
                        ›
                      </button>
                      <div className="focused-post-media-counter">
                        {focusedPostMediaIndex + 1} / {allItems.length}
                      </div>
                    </>
                  );
                }
                return null;
              })()}
            </div>

            {/* Action Buttons */}
            <div className={`focused-post-actions ${(() => {
              const mediaItems = getFocusedPostMediaItems();
              const current = mediaItems.length > 0 ? mediaItems[focusedPostMediaIndex] : null;
              return current && current.type === "video" ? "video-actions" : "";
            })()}`}>
              <button
                className={`focused-post-action-button like-button ${likedPosts.has(focusedPost.id) ? 'liked' : ''}`}
                onClick={handleLike}
              >
                <FaHeart />
              </button>
              <button className="focused-post-action-button" onClick={handleShare}>
                <FaShare />
              </button>
              {(() => {
                const mediaItems = getFocusedPostMediaItems();
                const current = mediaItems.length > 0 ? mediaItems[focusedPostMediaIndex] : null;
                if (current && current.type !== "video") {
                  return (
                    <button className="focused-post-action-button" onClick={handleFullScreen}>
                      <FaExpand />
                    </button>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Create Panel */}
      <div className={`create-panel-container ${isMinimized ? "minimized" : ""}`}>
        {!isMinimized ? (
          <>
            <button className="close-create-panel" onClick={handleMinimize}>×</button>
            <CreatePanel
              userProfile={userProfile}
              onContentChange={savePanelContent}
              onClearContent={clearSavedContent}
            />
          </>
        ) : (
          <div className="floating-plus-container">
            <button className="floating-plus" onClick={handleExpand}>+</button>
            {hasSavedContent && (
              <div className="saved-content-indicator" onClick={handleExpand}>
                📄
              </div>
            )}
          </div>
        )}
      </div>

      <div className="feed-layout">
        {/* Floating Sidebar Button */}
        <FloatingSidebarButton
          userProfile={userProfile}
          onFilterChange={(filter) => {
            // Filter change doesn't reload posts - just updates filter state
            // Posts are already cached and will be filtered in render
            setFilterType(filter);
            localStorage.setItem('feed_selected_filter', filter);
          }}
          onViewChange={(view) => {
            setSelectedView(view);
            localStorage.setItem('feed_selected_view', view);
          }}
        />

        <main className={`feed-container ${selectedView === 'Masonry' ? 'masonry-mode' : ''}`} ref={feedContainerRef}>
          {/* Main Feed */}
          <div
            className={`feed-list ${focusedPost ? 'blurred-background post-focused' : ''} ${selectedView === 'Grid' ? 'grid-view' : ''} ${selectedView === 'Masonry' ? 'masonry-view' : 'fixed-columns'} ${!isMinimized ? 'create-panel-expanded' : ''}`}
            ref={feedListRef}
            style={selectedView === 'Masonry' ? { "--masonry-cols": masonryCount } : (selectedView === 'Grid' ? { "--grid-cols": columnCount } : undefined)}
          >
            {selectedView === 'Masonry' ? (
              (() => {
                return masonryColumns.map((colItems, colIndex) => {
                  const durations = [130, 110, 120, 125, 115, 122];
                  const duration = durations[colIndex] || 120;
                  return (
                    <div
                      key={`masonry-col-${colIndex}`}
                      className="feed-masonry-column"
                      style={{
                        "--col-duration": `${duration}s`,
                        "--col-index": colIndex
                      }}
                    >
                      <div className="masonry-track">
                        {colItems.map((post, i) => (
                          <MasonryFeedItem
                            key={`masonry-${post.id}-${colIndex}-${i}`}
                            post={post}
                            onPostFocus={handlePostFocus}
                            selectedView={selectedView}
                            likedPosts={likedPosts}
                            onLikeClick={handleGridLikeClick}
                            onShareClick={handleGridShareClick}
                            currentUserId={currentUser?.id}
                            onBrokenMedia={handleBrokenMediaPost}
                          />
                        ))}
                      </div>
                    </div>
                  );
                });
              })()
            ) : (
              <>
                <div className="feed-columns-wrapper">
                  {(() => {
                    // Filter posts first, then distribute evenly across columns
                    const filteredPosts = feedPosts.filter(post => {
                      if (filterType === "Locked") return post.isLocked;
                      if (filterType === "All") {
                        // All filter: only images and videos (no links)
                        if (post.isLocked) return false;
                        const mediaList = Array.isArray(post.media) && post.media.length
                          ? post.media
                          : Array.isArray(post.compressedMedia)
                            ? post.compressedMedia
                            : [];
                        const items = mediaList.map(item => {
                          if (typeof item === "string") {
                            try { return JSON.parse(item); } catch { return { type: "link" }; }
                          }
                          return item;
                        });
                        // Exclude posts that only have links
                        const hasOnlyLinks = items.length > 0 && items.every(item => item?.type === "link");
                        const hasLinkInCaption = post.caption?.match(/https?:\/\/[^\s]+/);
                        if (hasOnlyLinks || (items.length === 0 && hasLinkInCaption)) return false;
                        return true;
                      }
                      if (post.isLocked) return false;

                      const mediaList = Array.isArray(post.media) && post.media.length
                        ? post.media
                        : Array.isArray(post.compressedMedia)
                          ? post.compressedMedia
                          : [];

                      const items = mediaList.map(item => {
                        if (typeof item === "string") {
                          try { return JSON.parse(item); } catch { return { type: "link" }; }
                        }
                        return item;
                      });

                      if (filterType === "Links") {
                        return items.some(item => item?.type === "link") || post.caption?.match(/https?:\/\/[^\s]+/);
                      }
                      if (filterType === "Videos") return items.some(item => item?.type === "video");
                      if (filterType === "Audio") return items.some(item => item?.type === "audio");
                      if (filterType === "Image") return items.some(item => item?.type === "image");
                      return true;
                    });

                    // Distribute posts evenly across columns (round-robin)
                    const columns = Array.from({ length: columnCount }, () => []);
                    filteredPosts.forEach((post, index) => {
                      const columnIndex = index % columnCount;
                      columns[columnIndex].push(post);
                    });

                    return columns.map((columnPosts, colIdx) => (
                      <div key={`col-${colIdx}`} className="feed-column">
                        {columnPosts.map((post, index) => (
                          <FeedItem
                            key={`grid-${post.id}-${colIdx}-${index}`}
                            post={post}
                            onPostFocus={handlePostFocus}
                            selectedView={selectedView}
                            likedPosts={likedPosts}
                            onLikeClick={handleGridLikeClick}
                            onShareClick={handleGridShareClick}
                            currentUserId={currentUser?.id}
                            onBrokenMedia={handleBrokenMediaPost}
                          />
                        ))}
                      </div>
                    ));
                  })()}
                </div>
                {/* Sentinel positioned after columns for proper infinite scroll */}
                <div ref={sentinelRef} style={{ height: 1, width: '100%' }} />
              </>
            )}
          </div>

          {loading && selectedView !== 'Masonry' && <div className="feed-loading">Loading...</div>}
          {!hasMore && selectedView !== 'Masonry' && <div className="feed-no-more">No more posts</div>}
        </main>
      </div>
    </>
  );
}

// ---------- caption helper ----------
function renderCaptionWithLinks(caption) {
  const parts = caption.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    part.match(/^https?:\/\/[^\s]+$/) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="caption-link"
        onClick={(e) => {
          if (part.includes('youtu.be') || part.includes('youtube.com')) {
            e.stopPropagation();
            e.preventDefault();
            window.open(part, "_blank");
          }
        }}
      >{part}</a>
    ) : part
  );
}

// Memoized FeedItem for masonry to prevent unnecessary re-renders
const MasonryFeedItem = React.memo(({ post, onPostFocus, selectedView, likedPosts, onLikeClick, onShareClick, currentUserId, onBrokenMedia }) => {
  return (
    <FeedItem
      post={post}
      onPostFocus={onPostFocus}
      selectedView={selectedView}
      likedPosts={likedPosts}
      onLikeClick={onLikeClick}
      onShareClick={onShareClick}
      currentUserId={currentUserId}
      onBrokenMedia={onBrokenMedia}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders unless post actually changes
  return prevProps.post.id === nextProps.post.id &&
    prevProps.post.uniqueKey === nextProps.post.uniqueKey;
});

const FeedItem = React.memo(React.forwardRef(({ post, onPostFocus, selectedView, likedPosts, onLikeClick, onShareClick, currentUserId, onBrokenMedia }, ref) => {
  const [idx, setIdx] = useState(0);
  const [convertedAmount, setConvertedAmount] = useState(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // Get audio URL - check both audioUrl and audio_filename
  const getAudioUrl = useCallback(() => {
    if (post.audioUrl) return post.audioUrl;
    if (post.audio_filename && post.audio_filename != null && post.audio_filename !== '') {
      const filename = String(post.audio_filename).trim();
      if (filename.length > 0) {
        const userId = post.postedById || post.user_id || post.postedByID || post.userId;
        if (userId) {
          return `https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/post-media/${userId}/${filename}`;
        }
      }
    }
    return null;
  }, [post.audioUrl, post.audio_filename, post.postedById, post.user_id, post.postedByID, post.userId]);

  const audioUrl = getAudioUrl();

  const handleVideoPlay = useCallback(() => {
    // Pause all other videos when this one starts playing
    const allVideos = document.querySelectorAll('.feed-video');
    allVideos.forEach(video => {
      if (video !== videoRef.current) {
        video.pause();
        video.currentTime = 0;
      }
    });
    // Also pause any playing audio
    const allAudios = document.querySelectorAll('.feed-post-audio');
    allAudios.forEach(audio => {
      if (audio !== audioRef.current) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    setIsAudioPlaying(false);
  }, []);

  const handleAudioPlay = useCallback((e) => {
    e.stopPropagation();
    if (!audioRef.current || !audioUrl) return;

    // Pause all other audios and videos
    const allAudios = document.querySelectorAll('.feed-post-audio');
    allAudios.forEach(audio => {
      if (audio !== audioRef.current) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    const allVideos = document.querySelectorAll('.feed-video');
    allVideos.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });

    if (isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    } else {
      audioRef.current.play();
      setIsAudioPlaying(true);
    }
  }, [isAudioPlaying, audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    const handlePlay = () => setIsAudioPlaying(true);
    const handlePause = () => setIsAudioPlaying(false);
    const handleEnded = () => setIsAudioPlaying(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl]);

  useEffect(() => {
    const fetchConversion = async () => {
      if (!post?.supportSegment || !post?.currency) return;

      const postCurrency = post.currency.toUpperCase();

      try {
        console.log('[Feed] fetching currency conversion from Supabase...');

        // Step 1: Get Winicoin to USD rate
        const { data: winiData, error: winiError } = await supabase
          .from('app_settings')
          .select('winicoinUsdExchangeRate')
          .eq('key', 'winicoins')
          .single();

        if (winiError) {
          console.error('[Feed] Supabase wini rate error:', winiError);
          return;
        }

        const winiUsdRate = winiData?.winicoinUsdExchangeRate;
        if (!winiUsdRate) return;

        // Step 2: Get USD to target currency rate
        const { data: currencyData, error: currencyError } = await supabase
          .from('currencies')
          .select('USDToCoinExchangeRate')
          .eq('currency', postCurrency)
          .single();

        if (currencyError) {
          console.error('[Feed] Supabase currency rate error:', currencyError);
          return;
        }

        const usdToCurrencyRate = currencyData?.USDToCoinExchangeRate;
        if (!usdToCurrencyRate) return;

        // ✅ Corrected: Convert Wini → USD → Target Currency
        const result = post.supportSegment * winiUsdRate * usdToCurrencyRate;

        setConvertedAmount(result.toFixed(2));

        console.log(`[Feed] currency conversion: ${post.supportSegment} Winis ≈ ${result.toFixed(2)} ${postCurrency}`);
      } catch (err) {
        console.error("[Feed] Conversion failed:", err);
      }
    };

    fetchConversion();
  }, [post]);




  useEffect(() => {
    setIdx(0);
  }, [post.id]);

  const rawList = Array.isArray(post.media) && post.media.length
    ? post.media
    : Array.isArray(post.compressedMedia)
      ? post.compressedMedia
      : [];

  let items = rawList
    .map(r => {
      if (typeof r === "string") {
        try {
          const parsed = JSON.parse(r);
          if (parsed && parsed.type && parsed.url) return parsed;
        } catch {
          return { type: "link", url: r };
        }
      }
      if (r && r.type && r.url) return r;
      return null;
    })
    .filter(Boolean);

  // Fallback: Extract link from caption if items is empty
  if (items.length === 0) {
    if (post.caption) {
      const match = post.caption.match(/https?:\/\/[^\s]+/);
      if (match) {
        items = [{ type: "link", url: match[0] }];
      }
    }
  }

  const length = items.length;
  const current = length > 0 ? items[idx] : null;

  const [linkThumbUrl, setLinkThumbUrl] = useState(null);
  const [linkType, setLinkType] = useState(null); // Track link type for fallback

  useEffect(() => {
    if (!current || current.type !== "link") {
      setLinkThumbUrl(null);
      setLinkType(null);
      return;
    }

    // Detect link type first for proper fallback
    const url = current.url.toLowerCase();
    let detectedType = 'generic';
    if (url.includes('youtube.com') || url.includes('youtu.be') || url.includes('/shorts/')) {
      detectedType = 'youtube';
    } else if (url.includes('instagram.com')) {
      detectedType = 'instagram';
    } else if (url.includes('tiktok.com')) {
      detectedType = 'tiktok';
    } else if (url.includes('facebook.com')) {
      detectedType = 'facebook';
    }
    setLinkType(detectedType);

    // Set default thumbnail immediately based on detected type
    let defaultThumb = "/default-link-placeholder.png";
    if (detectedType === 'youtube') defaultThumb = "/default-youtubeshorts-thumb.jpeg";
    else if (detectedType === 'instagram') defaultThumb = "/default-instagram-thumb.jpeg";
    else if (detectedType === 'tiktok') defaultThumb = "/default-tiktok-thumb.jpeg";
    else if (detectedType === 'facebook') defaultThumb = "/default-facebook-thumb2.png";

    // If we have a saved thumbnail URL, try it (but default is already set as fallback)
    if (current.thumbnailUrl) {
      setLinkThumbUrl(current.thumbnailUrl);
      return; // Use saved thumbnail, default is set if it fails
    }

    // No saved thumbnail, set default immediately
    setLinkThumbUrl(defaultThumb);

    // Fallback: Fetch thumbnail from external APIs if no saved thumbnail
    const linkUrl = current.url;

    try {
      const u = new URL(current.url);
      const host = u.hostname.replace(/^www\./, "");
      let videoId = null;

      // ✅ YouTube Shorts support
      if (host === "youtube.com" || host === "m.youtube.com" || host.endsWith("youtube.com")) {
        const shortsMatch = u.pathname.match(/^\/shorts\/([^\/\?]+)/);
        if (shortsMatch) {
          videoId = shortsMatch[1];
          // Set default first, then try YouTube thumbnail
          setLinkType('youtube');
          setLinkThumbUrl("/default-youtubeshorts-thumb.jpeg");
          if (videoId) {
            // Try YouTube thumbnail, but default is already set if it fails
            const thumbUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
            setLinkThumbUrl(thumbUrl);
          }
          return;
        } else {
          // Regular YouTube video
          videoId = u.searchParams.get("v");
        }
      } else if (host === "youtu.be") {
        videoId = u.pathname.slice(1);
      }

      if (videoId) {
        setLinkType('youtube');
        // Set default first, then try YouTube thumbnail
        setLinkThumbUrl("/default-youtubeshorts-thumb.jpeg");
        const thumbUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
        setLinkThumbUrl(thumbUrl);
        return;
      }
    } catch (err) {
      // Invalid URL, will fall through to generic
    }

    // ✅ Instagram Reels/Posts (fetch thumbnail via backend)
    const igReel = linkUrl.match(/instagram\.com\/(?:reel|p|tv)\/([^\/?]+)/);
    if (igReel) {
      setLinkType('instagram');
      // Set default immediately, then try to fetch
      setLinkThumbUrl("/default-instagram-thumb.jpeg");

      // Fetch via backend to avoid CORS issues
      fetch(
        `${BACKEND_API_URL}/api/fetch-link-thumbnail?url=${encodeURIComponent(linkUrl)}`
      )
        .then((r) => {
          if (!r.ok) throw new Error('Failed to fetch thumbnail');
          return r.json();
        })
        .then((data) => {
          if (data.thumbnail_url) {
            setLinkThumbUrl(data.thumbnail_url);
          } else if (data.default) {
            setLinkThumbUrl(data.default);
          }
        })
        .catch(() => {
          // Already set to default, no need to change
        });
      return;
    }

    // ✅ TikTok (fetch thumbnail via backend)
    const ttPatterns = [
      /tiktok\.com\/@[^\/]+\/video\/(\d+)/,              // Standard URL
      /vm\.tiktok\.com\/[^\/]+/,                         // VM short URL
      /vt\.tiktok\.com\/[^\/]+/,                         // VT short URL
      /www\.tiktok\.com\/t\/[^\/]+/,                     // T short URL
      /tiktok\.com\/t\/[^\/]+/                           // T short URL (no www)
    ];

    const isTikTok = ttPatterns.some(pattern => pattern.test(linkUrl));
    if (isTikTok) {
      setLinkType('tiktok');
      // Set default immediately, then try to fetch
      setLinkThumbUrl("/default-tiktok-thumb.jpeg");

      // Fetch via backend to avoid CORS issues
      fetch(
        `${BACKEND_API_URL}/api/fetch-link-thumbnail?url=${encodeURIComponent(linkUrl)}`
      )
        .then((r) => {
          if (!r.ok) throw new Error('Failed to fetch thumbnail');
          return r.json();
        })
        .then((data) => {
          if (data.thumbnail_url) {
            setLinkThumbUrl(data.thumbnail_url);
          } else if (data.default) {
            setLinkThumbUrl(data.default);
          }
        })
        .catch(() => {
          // Already set to default, no need to change
        });
      return;
    }

    // ✅ Facebook (various formats: share links, watch videos, posts, etc.)
    try {
      const u = new URL(linkUrl);
      const host = u.hostname.replace(/^www\./, "");
      if (host === "facebook.com" || host === "m.facebook.com" || host.endsWith("facebook.com")) {
        setLinkType('facebook');
        // Set default immediately to prevent broken images
        setLinkThumbUrl("/default-facebook-thumb2.png");

        // Check for various Facebook link patterns
        const fbPatterns = [
          /facebook\.com\/watch\/\?v=(\d+)/,           // Watch videos
          /facebook\.com\/share\/r\/[^\/]+/,           // Share links
          /facebook\.com\/.*\/videos\/\d+/,            // Video posts
          /facebook\.com\/.*\/posts\/[^\/]+/,          // Posts
          /facebook\.com\/.*\/permalink\/\d+/,         // Permalink posts
          /facebook\.com\/.*\/photos\/[^\/]+/,         // Photo posts
          /facebook\.com\/.*\/reel\/[^\/]+/,          // Reels
        ];

        const isFacebook = fbPatterns.some(pattern => pattern.test(linkUrl));
        // Always try to fetch for Facebook links, even if pattern doesn't match (catch-all)
        console.log('[Feed] Facebook link detected:', linkUrl, 'Pattern match:', isFacebook);

        // Fetch via backend to avoid CORS issues
        fetch(
          `${BACKEND_API_URL}/api/fetch-link-thumbnail?url=${encodeURIComponent(linkUrl)}`
        )
          .then((r) => {
            console.log('[Feed] Backend response status:', r.status);
            if (!r.ok) {
              const errorText = r.text();
              console.error('[Feed] Backend error:', errorText);
              throw new Error('Failed to fetch thumbnail');
            }
            return r.json();
          })
          .then((data) => {
            console.log('[Feed] Backend response data:', data);
            if (data.thumbnail_url) {
              console.log('[Feed] Setting thumbnail URL:', data.thumbnail_url);
              setLinkThumbUrl(data.thumbnail_url);
            } else if (data.default) {
              console.log('[Feed] Using default thumbnail:', data.default);
              setLinkThumbUrl(data.default);
            }
          })
          .catch((err) => {
            console.error('[Feed] Thumbnail fetch error:', err);
            // Already set to default, no need to change
          });
        return;
      }
    } catch (err) {
      // Invalid URL, will fall through to generic
    }

    // Generic fallback - always set a default
    setLinkType('generic');
    setLinkThumbUrl("/default-link-placeholder.png");
  }, [current]);




  const prev = e => { e.stopPropagation(); setIdx(i => (i - 1 + length) % length); };
  const next = e => { e.stopPropagation(); setIdx(i => (i + 1) % length); };

  const handleClick = () => {
    if (post.isLocked) return;

    // Pause all videos when opening focused view
    const allVideos = document.querySelectorAll('.feed-video');
    allVideos.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });

    // Check if post has media (images/videos) - check both media_names and media array
    const hasMedia = (post.media_names && Array.isArray(post.media_names) && post.media_names.length > 0) ||
      (post.media && Array.isArray(post.media) && post.media.length > 0) ||
      (post.compressedMedia && Array.isArray(post.compressedMedia) && post.compressedMedia.length > 0);

    // Check if there are non-link items (images/videos) in the items array
    const hasNonLinkMedia = items.some(item => item?.type && item.type !== "link");

    // Only open link in new tab if post has NO media (only link, no images/videos)
    // If post has both media AND link, open focused view normally
    if (!hasMedia && !hasNonLinkMedia) {
      const linkItem = items.find(item => item?.type === "link");
      if (linkItem && linkItem.url) {
        window.open(linkItem.url, "_blank", "noopener,noreferrer");
        return;
      }
      // Fallback: check caption link if no link in items
      if (post.caption) {
        const linkMatch = post.caption.match(/https?:\/\/[^\s]+/);
        if (linkMatch && linkMatch[0]) {
          window.open(linkMatch[0], "_blank", "noopener,noreferrer");
          return;
        }
      }
    }

    // If post has media (images/videos), open focused view normally
    onPostFocus(post);
  };

  // Don't render invalid posts
  if (!isValidSupabasePost(post)) {
    return null;
  }

  // Extra guard: in Grid view, skip posts that have no displayable media item.
  // These are the \"broken\" tiles with no image/video that can appear when
  // a post is missing media but is still in cached state.
  if (selectedView === 'Grid' && !current) {
    console.warn('[Feed] Skipping grid post with no media to display', {
      id: post?.id,
      user_id: post?.user_id,
      media: post?.media,
      compressedMedia: post?.compressedMedia,
      media_names: post?.media_names,
      caption: post?.caption
    });
    return null;
  }

  return (
    <div
      className={`feed-item ${post.isLocked ? 'locked-post' : ''} ${selectedView === 'Grid' ? 'grid-item' : ''}`}
      ref={ref}
      onClick={handleClick}
    >
      <div className="feed-item-header">
        <Link
          to={`/profile/${post.postedById}`}
          className="username-link"
          target="_blank"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={post.postedByProfileImg}
            alt={post.postedByUsername}
            className="feed-avatar"
          />
          <span className="feed-username">{post.postedByUsername}</span>
        </Link>
      </div>

      <div className="feed-media-container">
        {current && (
          <>
            {!post.isLocked && length > 1 && (
              <button className="feed-carousel-arrow left" onClick={prev}>‹</button>
            )}
            {current.type === "image" && (
              <img
                src={current.url}
                alt=""
                className={`feed-image ${post.isLocked ? 'blurred' : ''}`}
                loading="lazy"
                decoding="async"
                onError={() => {
                  if (post?.id && typeof onBrokenMedia === 'function') {
                    console.warn('[Feed] Image failed to load for post. Hiding post:', post.id, current?.url);
                    onBrokenMedia(post.id);
                  }
                }}
              />
            )}
            {current.type === "video" && (
              <>
                <video
                  key={`video-${post.id}-${idx}-${current.url}`}
                  ref={videoRef}
                  onPlay={handleVideoPlay}
                  controls={!post.isLocked}
                  src={current.url}
                  className={`feed-video ${post.isLocked ? 'blurred' : ''}`}
                  preload="metadata"
                  playsInline
                  onError={() => {
                    if (post?.id && typeof onBrokenMedia === 'function') {
                      console.warn('[Feed] Video failed to load for post. Hiding post:', post.id, current?.url);
                      onBrokenMedia(post.id);
                    }
                  }}
                  onClick={(e) => {
                    // Always open focused mode for videos
                    e.stopPropagation();
                    e.preventDefault();
                    // Pause the clicked inline video
                    if (videoRef.current) {
                      try {
                        videoRef.current.pause();
                        videoRef.current.currentTime = 0;
                      } catch (_) { }
                      // Double-ensure pause after browser default handlers
                      requestAnimationFrame(() => {
                        try { videoRef.current && videoRef.current.pause(); } catch (_) { }
                      });
                    }
                    // Pause all other videos
                    const allVideos = document.querySelectorAll('.feed-video');
                    allVideos.forEach(v => { if (v !== videoRef.current) { v.pause(); } });
                    // Open focused view for video posts
                    onPostFocus(post);
                  }}
                />
                {/* Mobile Interaction Overlay for Video */}
                <div
                  className="mobile-video-overlay"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    // Pause video if playing
                    if (videoRef.current) {
                      try { videoRef.current.pause(); videoRef.current.currentTime = 0; } catch (_) { }
                    }
                    const allVideos = document.querySelectorAll('.feed-video');
                    allVideos.forEach(v => { v.pause(); });
                    onPostFocus(post);
                  }}
                />
              </>
            )}
            {current.type === "audio" && (
              <div className="feed-audio-container">
                <audio controls src={current.url} className="feed-audio" />
              </div>
            )}
            {current.type === "link" && (
              <div
                className={`feed-link-thumb ${post.isLocked ? 'blurred' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(current.url, "_blank");
                  e.preventDefault();
                }}
              >
                <img
                  src={(() => {
                    // Priority: saved thumbnail > fetched thumbnail > default based on type
                    if (current.thumbnailUrl) return current.thumbnailUrl;
                    if (linkThumbUrl) return linkThumbUrl;

                    // Determine default based on link type or URL
                    let defaultType = linkType;
                    if (!defaultType && current?.url) {
                      const url = current.url.toLowerCase();
                      if (url.includes('youtube.com') || url.includes('youtu.be') || url.includes('/shorts/')) {
                        defaultType = 'youtube';
                      } else if (url.includes('instagram.com')) {
                        defaultType = 'instagram';
                      } else if (url.includes('tiktok.com')) {
                        defaultType = 'tiktok';
                      } else if (url.includes('facebook.com')) {
                        defaultType = 'facebook';
                      } else {
                        defaultType = 'generic';
                      }
                    }

                    // Return appropriate default
                    if (defaultType === 'youtube') return "/default-youtubeshorts-thumb.jpeg";
                    if (defaultType === 'instagram') return "/default-instagram-thumb.jpeg";
                    if (defaultType === 'tiktok') return "/default-tiktok-thumb.jpeg";
                    if (defaultType === 'facebook') return "/default-facebook-thumb2.png";
                    return "/default-link-placeholder.png";
                  })()}
                  alt="link thumbnail"
                  className="feed-link-thumb-img"
                  onError={(e) => {
                    // Prevent infinite loop
                    if (e.target.src.includes('default-')) return;

                    // Determine fallback type from URL if linkType not set
                    let fallbackType = linkType;
                    if (!fallbackType && current?.url) {
                      const url = current.url.toLowerCase();
                      if (url.includes('youtube.com') || url.includes('youtu.be') || url.includes('/shorts/')) {
                        fallbackType = 'youtube';
                      } else if (url.includes('instagram.com')) {
                        fallbackType = 'instagram';
                      } else if (url.includes('tiktok.com')) {
                        fallbackType = 'tiktok';
                      } else if (url.includes('facebook.com')) {
                        fallbackType = 'facebook';
                      } else {
                        fallbackType = 'generic';
                      }
                    }

                    // Set appropriate default based on type
                    if (fallbackType === 'youtube') {
                      e.target.src = "/default-youtubeshorts-thumb.jpeg";
                    } else if (fallbackType === 'instagram') {
                      e.target.src = "/default-instagram-thumb.jpeg";
                    } else if (fallbackType === 'tiktok') {
                      e.target.src = "/default-tiktok-thumb.jpeg";
                    } else if (fallbackType === 'facebook') {
                      e.target.src = "/default-facebook-thumb2.png";
                    } else {
                      e.target.src = "/default-link-placeholder.png";
                    }
                  }}
                />
              </div>
            )}
            {!post.isLocked && length > 1 && (
              <button className="feed-carousel-arrow right" onClick={next}>›</button>
            )}
          </>
        )}

        {/* 🔒 Overlay for locked posts */}
        {post.isLocked && selectedView !== 'Masonry' && (
          <div className="locked-overlay">
            <div className="lock-icon-centered">
              <svg className="lock-icon" viewBox="0 0 24 24">
                <path d="M12 1C8.14 1 5 4.14 5 8v1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V10c0-.55-.45-1-1-1h-1V8c0-3.86-3.14-7-7-7zm0 2c2.76 0 5 2.24 5 5v1H7V8c0-2.76 2.24-5 5-5zm0 10c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z" />
              </svg>
            </div>

            <div className="locked-payment-box">
              <WaveInsideLockedBox
                color="#2196f3"
                backgroundColor="rgba(0, 0, 0, 0.4)"
                onComplete={() => {
                  console.log("Unlock triggered!");
                  // call unlock modal here
                }}
              >
                <div className="lock-payment-first-row">
                  <svg className="small-lock-icon" viewBox="0 0 24 24">
                    <path d="M12 1C8.14 1 5 4.14 5 8v1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V10c0-.55-.45-1-1-1h-1V8c0-3.86-3.14-7-7-7zm0 2c2.76 0 5 2.24 5 5v1H7V8c0-2.76 2.24-5 5-5zm0 10c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z" />
                  </svg>
                  <span className="locked-payment-text">Pay {(+post.supportSegment).toFixed(2) || '0.00'} Winis to unlock</span>
                </div>
                <span className="locked-payment-subtext">
                  ≈ {convertedAmount ?? '...'} {post.currency || ''}
                </span>
              </WaveInsideLockedBox>
            </div>

          </div>
        )}
      </div>

      {
        !post.isLocked && post.caption && selectedView !== 'Grid' && selectedView !== 'Masonry' && (
          <p className="feed-caption">
            <strong>{post.postedByUsername} </strong>
            {renderCaptionWithLinks(post.caption)}
          </p>
        )
      }

      {
        !post.isLocked && selectedView !== 'Grid' && selectedView !== 'Masonry' && (
          <div className="feed-timestamp">
            {formatTimestamp(post.timestamp ? new Date(post.timestamp) : null)}
          </div>
        )
      }

      {/* Grid View Action Buttons */}
      {
        selectedView === 'Grid' && !post.isLocked && (
          <>
            {/* Audio Play Button - Left Side */}
            {audioUrl && (
              <div className="grid-audio-button-container">
                <button
                  className="grid-action-button grid-audio-button"
                  onClick={handleAudioPlay}
                >
                  {isAudioPlaying ? <FaPause /> : <FaPlay />}
                </button>
                {/* Hidden audio element for playback */}
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  className="feed-post-audio"
                  preload="none"
                />
              </div>
            )}
            {/* Like and Share Buttons - Right Side */}
            <div className="grid-action-buttons">
              <button
                className={`grid-action-button like-button ${likedPosts.has(post.id) ? 'liked' : ''}`}
                onClick={(e) => onLikeClick(e, post)}
              >
                <FaHeart />
              </button>
              <button
                className="grid-action-button grid-share-button"
                onClick={(e) => onShareClick(e, post)}
              >
                <FaShare />
              </button>
            </div>
          </>
        )
      }
    </div >

  );
}));

